import React, { useState, useEffect, useRef, useMemo } from "react";
import jsPDF from "jspdf";
import { Modal } from "react-bootstrap";
import { useLocation } from "react-router-dom";
import {
  Search as SearchIcon,
  Visibility as ViewIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  BoltOutlined as BoltIcon,
  Photo as PhotoIcon,
  Description as DescriptionIcon,
  TrendingUp as TrendingUpIcon,
  Assignment as AssignmentIcon,
  AccessTime as TimeIcon,
  Download as DownloadIcon,
  Close as CloseIcon,
  Build as BuildIcon,
  CleaningServices as CleaningIcon,
  Warning as WarningIcon,
  CheckBox as CheckBoxIcon,
  ArrowBack as ArrowBackIcon,
  Business as BusinessIcon,
  DateRange as DateRangeIcon,
  KeyboardArrowRight as ArrowRightIcon,
  Event as EventIcon,
  Engineering as EngineeringIcon,
  Notes as NotesIcon,
  Add as AddIcon,
  ThumbUp as ApproveIcon,
  ThumbDown as RejectIcon,
  HourglassEmpty as PendingIcon,
} from "@mui/icons-material";
import { format } from "date-fns";
import { getAuthHeader } from "../../helpers/auth";
import axios from "axios";

// 🔥 AUTO-FALLBACK IMAGE COMPONENT (Folder finding logic)
const AutoFallbackImage = ({ photoRaw, alt, isEditing, onDelete, onClick, customStyle }) => {
  const [errorCount, setErrorCount] = React.useState(0);
  
  // Har kism ke format (JSON, Object, Array, String) ko clean karna
  let cleanStr = "";
  if (photoRaw && typeof photoRaw === 'object') {
    cleanStr = photoRaw.url || photoRaw.path || photoRaw.name || String(photoRaw);
  } else {
    cleanStr = String(photoRaw);
  }
  
  // Extra quotes ya brackets clean karna
  cleanStr = cleanStr.replace(/["\[\]\\]/g, "").replace(/%22/g, "").replace(/%5D/g, "").replace(/%5B/g, "").trim();
  let filename = cleanStr.split("/").pop();

  // Sab possible paths ki list
  const candidates = [];
  if (cleanStr.startsWith("http")) candidates.push(cleanStr);
  if (cleanStr.startsWith("/")) candidates.push(`https://app.sowashusa.com${cleanStr}`);
  
  candidates.push(`https://app.sowashusa.com/uploads/job-photos/${filename}`);
  candidates.push(`https://app.sowashusa.com/uploads/maintenance/${filename}`);
  candidates.push(`https://app.sowashusa.com/uploads/sld-reports/${filename}`); // Agar naya folder ho
  candidates.push(`https://app.sowashusa.com/uploads/signatures/${filename}`);
  
  // Duplicate paths hatana
  const uniqueCandidates = [...new Set(candidates)];
  const currentSrc = uniqueCandidates[errorCount] || uniqueCandidates[uniqueCandidates.length - 1];

  const defaultStyle = { width: "100%", height: "150px", objectFit: "cover", borderRadius: "12px", cursor: isEditing ? "default" : "pointer", border: "2px solid #E5E7EB" };

  return (
    <div style={{ position: 'relative' }}>
      <img
        src={currentSrc}
        alt={alt}
        style={customStyle || defaultStyle}
        onClick={() => {
          if (onClick) onClick(currentSrc);
          else if (!isEditing) window.open(currentSrc, "_blank");
        }}
        onError={() => {
          // Agar image na milay tou aglay folder mein khud check karega
          if (errorCount < uniqueCandidates.length - 1) {
            setErrorCount(prev => prev + 1);
          }
        }}
      />
      {isEditing && onDelete && (
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          style={{ position: 'absolute', top: '5px', right: '5px', background: '#EF4444', color: 'white', border: 'none', borderRadius: '50%', width: '24px', height: '24px', cursor: 'pointer', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10 }}
        >✕</button>
      )}
    </div>
  );
};

const JobHistory = () => {
  const config = getAuthHeader();
  const location = useLocation();

  const [detailsLoading, setDetailsLoading] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  const [stats, setStats] = useState({
    total: 0,
    completed: 0,
    inProgress: 0,
    scheduled: 0,
  });

  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [toast, setToast] = useState(null);

  // ── Approval state ──────────────────────────────────────────
  const [filterApproval, setFilterApproval]     = useState("all");
  const [approving, setApproving]               = useState(false);
  const [rejecting, setRejecting]               = useState(false);
  const [showRejectInput, setShowRejectInput]   = useState(false);
  const [rejectReason, setRejectReason]         = useState("");

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  // ── Approval handlers ───────────────────────────────────────
  const handleApprove = async () => {
    if (!selectedJob) return;
    setApproving(true);
    try {
      await axios.put(
        `/api/ci-admin/job-approvals/${selectedJob.schedule_id}/approve`,
        {},
        config
      );
      showToast("success", "✅ Job approved — now visible to client");
      setSelectedJob(prev => ({ ...prev, approval_status: "approved" }));
      fetchJobs();
    } catch (err) {
      showToast("error", err.response?.data?.error || "Failed to approve job");
    } finally {
      setApproving(false);
    }
  };

  const handleReject = async () => {
    if (!selectedJob) return;
    setRejecting(true);
    try {
      await axios.put(
        `/api/ci-admin/job-approvals/${selectedJob.schedule_id}/reject`,
        { reason: rejectReason.trim() || null },
        config
      );
      showToast("success", "🚫 Job rejected — hidden from client");
      setSelectedJob(prev => ({ ...prev, approval_status: "rejected", rejection_reason: rejectReason.trim() || null }));
      setShowRejectInput(false);
      setRejectReason("");
      fetchJobs();
    } catch (err) {
      showToast("error", err.response?.data?.error || "Failed to reject job");
    } finally {
      setRejecting(false);
    }
  };

  const handleUndoApproval = async () => {
    if (!selectedJob) return;
    try {
      await axios.put(
        `/api/ci-admin/job-approvals/${selectedJob.schedule_id}/undo`,
        {},
        config
      );
      showToast("success", "↩️ Approval reset to pending");
      setSelectedJob(prev => ({ ...prev, approval_status: "pending", rejection_reason: null }));
      fetchJobs();
    } catch (err) {
      showToast("error", err.response?.data?.error || "Failed to undo approval");
    }
  };

  // ── Approval badge config ───────────────────────────────────
  const getApprovalBadge = (approval_status) => {
    switch (approval_status) {
      case "approved": return { label: "Approved",  color: "#059669", bg: "#ECFDF5", border: "#6EE7B7", icon: "✓" };
      case "rejected": return { label: "Rejected",  color: "#DC2626", bg: "#FEF2F2", border: "#FECACA", icon: "✕" };
      default:         return { label: "Pending",   color: "#D97706", bg: "#FFFBEB", border: "#FDE68A", icon: "⏳" };
    }
  };



const JobEfficiencyTimer = ({ job }) => {
  const [timerData, setTimerData] = useState({ 
    remainingTime: 0, 
    isExceeded: false, 
    arrivalDiffMinutes: 0,
    totalTimeTaken: 0,
    efficiencyScore: 0
  });

  const SHIFT_CONFIG = {
    morning:   { label: 'Shift: 7 AM',  startHour: 7  },
    afternoon: { label: 'Shift: 11 PM', startHour: 11 },
    evening:   { label: 'Shift: 3 PM',  startHour: 15 },
    night:     { label: 'Shift: 7 PM',  startHour: 19 },
    overnight: { label: 'Shift: 10 PM', startHour: 22 },
  };

  const getShiftConfig = (shift) => SHIFT_CONFIG[shift] || { label: 'Shift: N/A', startHour: null };

  useEffect(() => {
    const calculate = () => {
      if (!job.started_at) return;
      
      // 1. Shift Arrival Logic
      const actualReached = new Date(job.reached_location_at);
      const shiftStartTime = new Date(actualReached);
      
      const shiftConfig = getShiftConfig(job.shift);
      if (shiftConfig.startHour !== null) {
        shiftStartTime.setHours(shiftConfig.startHour, 0, 0, 0);
      }

      // positive = late, negative = early, 0 = on time
      const diffMs = actualReached - shiftStartTime;
      const arrivalDiffMinutes = Math.floor(diffMs / 60000);
      
      // 2. Efficiency Logic
      const teamSize = 4;
      const totalManHours = parseFloat(job.total_man_hours) || 32;
      const targetSec = (totalManHours / teamSize) * 3600;
      
      const endTime = job.completed_at ? new Date(job.completed_at) : new Date();
      const elapsedSec = Math.floor((endTime - new Date(job.reached_location_at)) / 1000);
      const remaining = targetSec - elapsedSec;
      const efficiency = elapsedSec > 0 ? Math.round((targetSec / elapsedSec) * 100) : 100;

      setTimerData({ 
        remainingTime: Math.abs(remaining), 
        isExceeded: remaining < 0, 
        arrivalDiffMinutes,
        totalTimeTaken: elapsedSec,
        efficiencyScore: efficiency
      });
    };

    if (job.completed_at) {
      calculate();
    } else {
      const itv = setInterval(calculate, 1000);
      calculate();
      return () => clearInterval(itv);
    }
  }, [job]);

  const formatDisplayTime = (totalSeconds) => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const formatDelay = (totalMins) => {
    if (totalMins < 60) return `${totalMins}m`;
    const h = Math.floor(totalMins / 60);
    const m = totalMins % 60;
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  };

  const getArrivalStatus = () => {
    const diff = timerData.arrivalDiffMinutes;
    if (diff > 0)  return { label: `${formatDelay(diff)} Late`,              color: '#D97706', bg: '#FFFBEB', border: '#FDE68A' };
    if (diff < 0)  return { label: `${formatDelay(Math.abs(diff))} Early`,   color: '#2563EB', bg: '#EFF6FF', border: '#BFDBFE' };
                   return { label: 'On Time',                                 color: '#10B981', bg: '#F0FDF4', border: '#BBF7D0' };
  };

  if (!job.started_at) return (
    <div style={{ marginTop: '12px', padding: '12px', borderRadius: '12px', background: '#F3F4F6', textAlign: 'center', border: '1px dashed #D1D5DB' }}>
      <span style={{ fontSize: '13px', color: '#6B7280', fontWeight: '600' }}>🕒 Waiting to start...</span>
    </div>
  );

  const isDone = !!job.completed_at;
  const arrivalStatus = getArrivalStatus();

  return (
    <div style={{ 
      marginTop: '12px', 
      padding: '16px', 
      borderRadius: '16px', 
      background: isDone ? '#F8FAFC' : (timerData.isExceeded ? '#FFF1F2' : '#F0FDF4'), 
      border: `2px solid ${isDone ? '#E2E8F0' : (timerData.isExceeded ? '#FECACA' : '#BBF7D0')}`,
      boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
        <div>
          <span style={{ fontSize: '10px', fontWeight: '800', color: isDone ? '#64748B' : (timerData.isExceeded ? '#E11D48' : '#166534'), textTransform: 'uppercase' }}>
            {isDone ? '🏁 Total Time Taken' : (timerData.isExceeded ? '⚠️ Overtime' : '⏱️ Time Remaining')}
          </span>
          <div style={{ fontSize: '24px', fontWeight: '900', color: isDone ? '#1E293B' : (timerData.isExceeded ? '#BE123C' : '#15803D'), fontFamily: 'monospace', lineHeight: 1 }}>
            {isDone 
              ? formatDisplayTime(timerData.totalTimeTaken) 
              : (timerData.isExceeded 
                  ? `-${formatDisplayTime(timerData.remainingTime)}` 
                  : formatDisplayTime(timerData.remainingTime))}
          </div>
        </div>

        {isDone && (
          <div style={{ textAlign: 'right', background: timerData.efficiencyScore >= 100 ? '#DCFCE7' : '#FEF2F2', padding: '6px 12px', borderRadius: '10px', border: `1px solid ${timerData.efficiencyScore >= 100 ? '#86EFAC' : '#FECACA'}` }}>
            <span style={{ fontSize: '9px', fontWeight: '800', display: 'block', color: timerData.efficiencyScore >= 100 ? '#166534' : '#991B1B' }}>EFFICIENCY</span>
            <span style={{ fontSize: '16px', fontWeight: '900', color: timerData.efficiencyScore >= 100 ? '#15803D' : '#DC2626' }}>{timerData.efficiencyScore}%</span>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: '8px', borderTop: '1px solid rgba(0,0,0,0.05)', paddingTop: '10px' }}>
        
        {/* Arrival Status Card */}
        <div style={{ flex: 1, background: arrivalStatus.bg, padding: '6px 10px', borderRadius: '8px', border: `1px solid ${arrivalStatus.border}` }}>
          <span style={{ fontSize: '9px', color: '#64748B', fontWeight: '700', textTransform: 'uppercase', display: 'block' }}>
            {getShiftConfig(job.shift).label}
          </span>
          <span style={{ color: arrivalStatus.color, fontSize: '13px', fontWeight: '800' }}>
            {arrivalStatus.label}
          </span>
        </div>

        {/* Time Limit Card */}
        <div style={{ flex: 1, background: '#F8FAFC', padding: '6px 10px', borderRadius: '8px', border: '1px solid #E2E8F0', textAlign: 'center' }}>
          <span style={{ fontSize: '9px', color: '#64748B', fontWeight: '700', textTransform: 'uppercase', display: 'block' }}>Limit (4px)</span>
          <span style={{ color: '#475569', fontSize: '13px', fontWeight: '800' }}>{(parseFloat(job.total_man_hours || 32) / 4)}h</span>
        </div>

      </div>
    </div>
  );
};

  // Pakistan Standard Time is UTC+5 with no DST.
  // All date display and save operations are pinned to PKT so that neither
  // the browser's OS timezone nor the VPS server timezone affects the result.
  const PKT_OFFSET_MS = 5 * 60 * 60 * 1000;

  // Return an unambiguous UTC millisecond value from any DB timestamp string.
  // Handles: "2026-04-21T06:19:00.000Z", "+05:00", no-tz (treated as UTC).
  const toUtcMs = (ds) => {
    if (!ds) return NaN;
    let s = String(ds).trim().replace(' ', 'T').split('.')[0];
    if (!s.includes('Z') && !s.includes('+') && !/\d{2}:\d{2}$/.test(s.slice(-6))) {
      s += 'Z'; // no tz info at all -> assume UTC
    }
    return new Date(s).getTime();
  };

  // DB timestamp -> datetime-local string in PKT
  const formatDateTimeForInput = (dateString) => {
    if (!dateString) return '';
    const pktMs = toUtcMs(dateString) + PKT_OFFSET_MS;
    if (isNaN(pktMs)) return '';
    const d = new Date(pktMs);
    return [
      d.getUTCFullYear(),
      String(d.getUTCMonth() + 1).padStart(2, '0'),
      String(d.getUTCDate()).padStart(2, '0')
    ].join('-') + 'T' + [
      String(d.getUTCHours()).padStart(2, '0'),
      String(d.getUTCMinutes()).padStart(2, '0')
    ].join(':');
  };

  // DB timestamp -> date-only string in PKT
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    const pktMs = toUtcMs(dateString) + PKT_OFFSET_MS;
    if (isNaN(pktMs)) return '';
    const d = new Date(pktMs);
    return [
      d.getUTCFullYear(),
      String(d.getUTCMonth() + 1).padStart(2, '0'),
      String(d.getUTCDate()).padStart(2, '0')
    ].join('-');
  };

  // datetime-local string (user entered in PKT) -> UTC ISO string for DB
  const formatDateTimeForDB = (dateString) => {
    if (!dateString) return null;
    if (dateString.includes('Z') || dateString.includes('+')) return dateString;
    const [datePart, timePart] = dateString.split('T');
    if (!datePart || !timePart) return dateString;
    const [Y, M, D] = datePart.split('-').map(Number);
    const [h, mi] = timePart.split(':').map(Number);
    return new Date(Date.UTC(Y, M - 1, D, h, mi) - PKT_OFFSET_MS).toISOString();
  };

  const handleUpdate = async () => {
  setSaving(true);
  try {
    const payload = { ...editData };

    // Strip large photo arrays (managed via dedicated photo routes)
    ['before_photos','after_photos','temp_voltage_photos',
     'before_photo_url','after_photo_url','annotated_photo','annotated_photo_uri']
      .forEach(f => delete payload[f]);

    // Convert user-edited datetime-local fields (no tz suffix) from PKT -> UTC.
    // Fields that already have Z or + are left as-is.
    ['reached_location_at','started_at','before_photos_at','before_photo_at',
     'after_photos_at','after_photo_at','completed_at'].forEach(field => {
      const val = payload[field];
      if (val && typeof val === 'string' && val.includes('T') && !val.includes('Z') && !val.includes('+')) {
        payload[field] = formatDateTimeForDB(val);
      }
    });

    const response = await axios.put(
      `/api/schedule/edit/${editData.schedule_id}`,
      payload,
      config
    );

    if (response.data.success) {
      showToast('success', 'Job updated successfully');
      setIsEditing(false);
      setDetailsOpen(false);
      fetchJobs();
    }
  } catch (err) {
    console.error(err);
    showToast('error', 'Error updating job: ' + (err.response?.data?.message || err.message));
  } finally {
    setSaving(false);
  }
};

  // Upload image function
  const handleImageUpload = async (file, photoType) => {
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('photo', file);
    formData.append('schedule_id', editData.schedule_id);
    formData.append('photo_type', photoType);
    try {
      const response = await axios.post('/api/schedule/edit/upload-photo', formData, {
        headers: { ...config.headers, 'Content-Type': 'multipart/form-data' }
      });
      if (response.data.success) {
        let existing = [];
        try {
          existing = typeof editData[photoType] === 'string'
            ? JSON.parse(editData[photoType]) : (editData[photoType] || []);
        } catch (e) { existing = []; }
        existing.push(response.data.photoPath);
        setEditData({ ...editData, [photoType]: JSON.stringify(existing) });
        showToast('success', 'Photo uploaded successfully');
      }
    } catch (error) {
      console.error('Upload error:', error);
      showToast('error', 'Error uploading photo: ' + (error.response?.data?.message || error.message));
    } finally {
      setUploading(false);
    }
  };

  // Persist photo deletion immediately so reloading does not restore deleted photos
  const handlePhotoDelete = async (key, updatedPhotos) => {
    setEditData(prev => ({ ...prev, [key]: JSON.stringify(updatedPhotos) }));
    try {
      await axios.put(
        `/api/schedule/edit/${editData.schedule_id}/photos`,
        { photo_type: key, photos: updatedPhotos },
        config
      );
      showToast('success', 'Photo removed');
    } catch (err) {
      console.error('Photo delete error:', err);
      showToast('error', 'Failed to remove photo');
    }
  };

  useEffect(() => {
    if (selectedJob) {
      setEditData({ ...selectedJob });
    }
  }, [selectedJob]);

  const [viewMode, setViewMode] = useState("clients");
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [dateRange, setDateRange] = useState("all");
  const [browseMode, setBrowseMode] = useState("byClient");
  const [bulkDownloading, setBulkDownloading] = useState(false);
  const [bulkDownloadProgress, setBulkDownloadProgress] = useState({});
  const [bulkDone, setBulkDone] = useState(false);
  const debounceTimer = useRef(null);

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const paramClient = searchParams.get("client");
    const paramDate = searchParams.get("date");
    const paramSite = searchParams.get("site");

    if (paramClient && paramDate) {
      const dateObj = new Date(paramDate);
      if (!isNaN(dateObj)) {
        const monthStr = format(dateObj, "yyyy-MM");
        const dateStr = format(dateObj, "yyyy-MM-dd");

        setSelectedClient(paramClient);
        setSelectedMonth(monthStr);
        setSelectedDate(dateStr);
        setViewMode("jobs");

        if (paramSite) {
          setSearchQuery(paramSite);
        }
      }
    } else if (paramClient) {
      setSelectedClient(paramClient);
      setViewMode("months");
    }
  }, [location.search]);

  const cleanPhotoUrl = (url) => {
    if (!url) return "";
    let cleanUrl = url
      .toString()
      .trim()
      .replace(/["\[\]]/g, "")
      .replace(/%22/g, "")
      .replace(/%5D/g, "");
    if (cleanUrl.startsWith("http")) return cleanUrl;
    if (cleanUrl.startsWith("/")) return `https://app.sowashusa.com${cleanUrl}`;
    return `https://app.sowashusa.com/uploads/maintenance/${cleanUrl}`;
  };

  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      fetchJobs();
    }, 500);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [filterStatus, dateRange, searchQuery, filterType, filterApproval]);

  const fetchJobs = async () => {
    try {
      setLoading(true);

      const storedUser = localStorage.getItem("user");
      let clientId = null;

      if (storedUser) {
        const parsedUser = JSON.parse(storedUser);
        clientId = parsedUser.client_id || parsedUser.id;
      }

      const params = {};

      if (clientId) {
        params.client_id = clientId;
      }

      if (filterStatus && filterStatus !== "all") {
        params.status = filterStatus;
      }

      if (filterType && filterType !== "all") {
        params.job_type = filterType;
      }

      if (filterApproval && filterApproval !== "all") {
        params.approval_status = filterApproval;
      }

      if (dateRange && dateRange !== "all") {
        params.dateRange = dateRange;
      }

      if (searchQuery && searchQuery.trim() !== "") {
        params.search = searchQuery.trim();
      }

      const response = await axios.get("/api/schedule/history", {
        params,
        ...config,
      });
      setJobs(response.data.jobs);
    } catch (error) {
      console.error("Error fetching jobs:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get("/api/schedule/stats", config);
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const clientGroups = useMemo(() => {
    const groups = {};
    jobs.forEach((job) => {
      const name = job.client_name || "Unknown Client";
      if (!groups[name]) groups[name] = [];
      groups[name].push(job);
    });
    return groups;
  }, [jobs]);

  const monthGroups = useMemo(() => {
    if (!selectedClient || !clientGroups[selectedClient]) return {};
    const groups = {};
    clientGroups[selectedClient].forEach((job) => {
      const monthKey = format(new Date(job.scheduled_date), "yyyy-MM");
      if (!groups[monthKey]) groups[monthKey] = [];
      groups[monthKey].push(job);
    });
    return groups;
  }, [selectedClient, clientGroups]);

  const dateGroups = useMemo(() => {
    if (!selectedMonth || !monthGroups[selectedMonth]) return {};
    const groups = {};
    monthGroups[selectedMonth].forEach((job) => {
      const dateKey = format(new Date(job.scheduled_date), "yyyy-MM-dd");
      if (!groups[dateKey]) groups[dateKey] = [];
      groups[dateKey].push(job);
    });
    return groups;
  }, [selectedMonth, monthGroups]);

  const currentJobsList = useMemo(() => {
    if (!selectedDate || !dateGroups[selectedDate]) return [];
    return dateGroups[selectedDate];
  }, [selectedDate, dateGroups]);

  // Groups ALL jobs across all clients by date — used by the "By Date" browse mode
  const allDateGroups = useMemo(() => {
    const groups = {};
    jobs.forEach((job) => {
      const dateKey = format(new Date(job.scheduled_date), "yyyy-MM-dd");
      if (!groups[dateKey]) groups[dateKey] = [];
      groups[dateKey].push(job);
    });
    return groups;
  }, [jobs]);

  const handleClientClick = (clientName) => {
    setSelectedClient(clientName);
    setViewMode("months");
  };

  const handleMonthClick = (monthKey) => {
    setSelectedMonth(monthKey);
    setViewMode("dates");
  };

  const handleDateClick = (dateKey) => {
    setSelectedDate(dateKey);
    setViewMode("jobs");
  };

  const handleBack = () => {
    if (browseMode === "byDate") {
      setSelectedDate(null);
      setBulkDownloadProgress({});
      setBulkDone(false);
    } else {
      if (viewMode === "jobs") {
        setViewMode("dates");
        setSelectedDate(null);
      } else if (viewMode === "dates") {
        setViewMode("months");
        setSelectedMonth(null);
      } else if (viewMode === "months") {
        setViewMode("clients");
        setSelectedClient(null);
        setSearchQuery("");
      }
    }
  };

  const handleDownloadAllReports = async () => {
    const jobsList = allDateGroups[selectedDate] || [];
    if (!jobsList.length) return;
    setBulkDownloading(true);
    setBulkDone(false);
    const init = {};
    jobsList.forEach((j) => { init[j.schedule_id] = "pending"; });
    setBulkDownloadProgress(init);

    for (const job of jobsList) {
      setBulkDownloadProgress((prev) => ({ ...prev, [job.schedule_id]: "downloading" }));
      try {
        const response = await axios.get(`/api/fo-schedules/${job.schedule_id}/detail`, config);
        const fullJob = response.data.job;
        await generateAndDownloadPDF(fullJob);
        setBulkDownloadProgress((prev) => ({ ...prev, [job.schedule_id]: "done" }));
      } catch (err) {
        console.error("Error downloading report for job", job.schedule_id, err);
        setBulkDownloadProgress((prev) => ({ ...prev, [job.schedule_id]: "error" }));
      }
      await new Promise((r) => setTimeout(r, 400));
    }
    setBulkDone(true);
    setBulkDownloading(false);
    setTimeout(() => { setBulkDownloadProgress({}); setBulkDone(false); }, 4000);
  };

  const handleViewDetails = async (job) => {
    try {
      setDetailsLoading(true);
      setDetailsOpen(true);
      const response = await axios.get(
        `/api/fo-schedules/${job.schedule_id}/detail`,
        config,
      );
      setSelectedJob(response.data.job);
    } catch (error) {
      console.error("Error fetching job details:", error);
      showToast('error', error.response?.data?.message || error.message);
      setDetailsOpen(false);
    } finally {
      setDetailsLoading(false);
    }
  };

  // PDF Logic — shared between single download and bulk download
// ─────────────────────────────────────────────────────────────────
// DROP-IN REPLACEMENT — paste this entire block into JobHistory.jsx,
// replacing the existing generateAndDownloadPDF function.
// ─────────────────────────────────────────────────────────────────

const generateAndDownloadPDF = async (jobArg) => {
  const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const PW = pdf.internal.pageSize.getWidth();
  const PH = pdf.internal.pageSize.getHeight();
  const M = 12;
  let y = 0;

  const C = {
    purple: [101, 40, 169], pink:   [204, 40, 153],
    blue:   [37, 99, 235],  violet: [124, 58, 237],
    green:  [5, 150, 105],  amber:  [217, 119, 6],
    red:    [220, 38, 38],  dark:   [15, 23, 42],
    mid:    [51, 65, 85],   gray:   [100, 116, 139],
    silver: [203, 213, 225],snow:   [248, 250, 252],
    white:  [255, 255, 255],
  };

  const isMaint = (j) => j?.job_type?.toLowerCase() === "maintenance";

  const newPage = (need = 40) => {
    if (y + need > PH - 18) { pdf.addPage(); y = M; return true; }
    return false;
  };

  const truncate = (text, maxW, fontSize) => {
    pdf.setFontSize(fontSize);
    const words = (text || "").split(" ");
    let line = "";
    for (const w of words) {
      const test = line ? line + " " + w : w;
      if (pdf.getTextWidth(test) > maxW) return line + "...";
      line = test;
    }
    return line;
  };

  // ── Image pipeline ──────────────────────────────────────────────
  // Images are the only heavy part of the PDF. We (a) load them all in
  // PARALLEL up-front via a memoised cache, and (b) downscale each one to the
  // size it is actually placed at — cutting both generation time and file size.
  const imgCache = new Map(); // normalised url -> Promise<HTMLImageElement|null>

  const normUrl = (url) => {
    let u = (url || "").replace(/["\[\]]/g, "").trim();
    if (u && !u.startsWith("http"))
      u = `https://app.sowashusa.com/uploads/job-photos/${u}`;
    return u;
  };

  // Load + fully decode an image, memoised so prefetch and draw share one
  // request. Returns null on failure/timeout so we skip it rather than feeding
  // a half-loaded bitmap into jsPDF (a partial bitmap renders as TV-static).
  const loadImg = (url) => {
    const u = normUrl(url);
    if (!u) return Promise.resolve(null);
    if (imgCache.has(u)) return imgCache.get(u);
    const p = (async () => {
      try {
        const img = new Image();
        img.crossOrigin = "anonymous";
        const loaded = new Promise((res) => {
          img.onload = () => res(true);
          img.onerror = () => res(false);
          setTimeout(() => res(false), 12000);
        });
        img.src = u;
        if (!(await loaded) || !img.complete || !img.naturalWidth) return null;
        try { if (img.decode) await img.decode(); } catch {}
        return img;
      } catch { return null; }
    })();
    imgCache.set(u, p);
    return p;
  };

  // Warm the cache with a BOUNDED worker pool (not Promise.all). Firing every
  // request at once arms each image's load-timeout while the browser still has
  // it queued behind the ~6-per-host connection cap, so later images time out
  // and go missing. A pool of 6 means a timer only starts once that image is
  // actually being fetched.
  const prefetchImgs = async (urls, concurrency = 6) => {
    const list = (urls || []).filter(Boolean);
    let next = 0;
    const worker = async () => {
      while (next < list.length) {
        await loadImg(list[next++]);
      }
    };
    await Promise.all(
      Array.from({ length: Math.min(concurrency, list.length) }, worker)
    );
  };

  // Re-encode a decoded image to a CLEAN baseline-JPEG data URL via canvas,
  // downscaled to ~200 DPI for its placement width (mm) and never upscaled.
  // This both fixes the old "TV-static" corruption (jsPDF embeds a real JPEG /
  // DCTDecode stream) and keeps the file small. White-fill first so transparent
  // PNGs don't come out as black blocks.
  const imgToJpeg = (img, placeWmm) => {
    const targetPx = placeWmm ? Math.round((placeWmm / 25.4) * 200) : img.naturalWidth;
    const scale = Math.min(1, targetPx / img.naturalWidth);
    const w = Math.max(1, Math.round(img.naturalWidth * scale));
    const h = Math.max(1, Math.round(img.naturalHeight * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.imageSmoothingQuality = "high";
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(img, 0, 0, w, h);
    return canvas.toDataURL("image/jpeg", 0.72);
  };

  let imgAlias = 0; // unique alias per image -> avoids jsPDF cache collisions
  const drawImg = async (url, x, yy, w, h) => {
    const img = await loadImg(url);
    if (!img) return;
    try {
      pdf.addImage(imgToJpeg(img, w), "JPEG", x, yy, w, h, `img_${imgAlias++}`, "FAST");
    } catch {}
  };

  // Build the exact full URL that drawImg receives, so prefetch keys match.
  const buildFullUrl = (entry) => {
    let name = entry.url.replace(/["\[\]]/g, "").trim();
    if (name.includes("/")) name = name.split("/").pop();
    return name.startsWith("http") ? name : `${entry.basePath}/${name}`;
  };

  const parsePhotos = (raw) => {
    try { return typeof raw === "string" ? JSON.parse(raw) : (raw || []); }
    catch { return []; }
  };

  const fmtTime = (ts) => ts ? format(new Date(ts), "hh:mm a") : "—";
  const fmtDate = (ts) => ts ? format(new Date(ts), "MMM dd, yyyy") : "—";

  // ── FIND COMPANION ─────────────────────────────────────────────
  //
  // STEP 1 — jobArg already carries full detail when opened from the modal or
  //           the bulk loop, so only re-fetch when team_lead_name is missing.
  let mainFull = jobArg;
  if (!mainFull.team_lead_name) {
    try {
      const r = await axios.get(`/api/fo-schedules/${jobArg.schedule_id}/detail`, config);
      mainFull = r.data.job;
    } catch { /* fallback to jobArg */ }
  }

  const selDateStr = format(new Date(mainFull.scheduled_date), "yyyy-MM-dd");

  // STEP 2 — find all candidate jobs from jobs state that share
  //           same date + same client + same site + different id + completed
const candidates = jobs.filter(
  (j) =>
    j.status === "completed" &&
    j.schedule_id !== mainFull.schedule_id &&
    format(new Date(j.scheduled_date), "yyyy-MM-dd") === selDateStr &&
    j.client_name === mainFull.client_name
    // site match NAHI — same team alag sites pe bhi kaam kar sakti hai
);

  // STEP 3 — fetch all candidate details in PARALLEL, then pick the first that
  //           shares the same team lead (Promise.all preserves candidate order).
  let companionFull = null;
  if (candidates.length && mainFull.team_lead_name) {
    const details = await Promise.all(
      candidates.map((candidate) =>
        axios
          .get(`/api/fo-schedules/${candidate.schedule_id}/detail`, config)
          .then((r) => r.data.job)
          .catch(() => null)
      )
    );
    companionFull =
      details.find((cd) => cd && cd.team_lead_name === mainFull.team_lead_name) || null;
  }

  // ── ORDER: earlier arrival = Morning (left), later = Evening (right) ──
  const getArrivalMs = (job) =>
    new Date(job.reached_location_at || job.started_at || job.scheduled_date).getTime();

  let jobL = mainFull;
  let jobR = companionFull;
  if (companionFull && getArrivalMs(companionFull) < getArrivalMs(mainFull)) {
    jobL = companionFull;
    jobR = mainFull;
  }

  const isDual = !!jobR;
  const CW = isDual ? (PW - 2 * M - 5) / 2 : PW - 2 * M;
  const colJobs = isDual ? [jobL, jobR] : [jobL];
  const colColors = [
    { accent: C.blue,   bg: [239, 246, 255], border: [147, 197, 253] },
    { accent: C.violet, bg: [245, 243, 255], border: [196, 181, 253] },
  ];

  // ══════════════════════════════════════════════════════════════════
  // HEADER
  // ══════════════════════════════════════════════════════════════════
  const HH = 62;
  for (let i = 0; i < HH; i++) {
    const t = i / HH;
    pdf.setFillColor(
      Math.round(C.purple[0] + (C.pink[0] - C.purple[0]) * t),
      Math.round(C.purple[1] + (C.pink[1] - C.purple[1]) * t),
      Math.round(C.purple[2] + (C.pink[2] - C.purple[2]) * t)
    );
    pdf.rect(0, i, PW, 1, "F");
  }

  try {
    const logo = new Image(); logo.src = "/sowash-logo.png";
    pdf.addImage(logo, "PNG", PW / 2 - 20, 3, 40, 10);
  } catch {}

  pdf.setTextColor(...C.white);
  pdf.setFontSize(isDual ? 17 : 19);
  pdf.setFont("helvetica", "bold");
  pdf.text(isDual ? "Daily Soiling Report" : "Soiling Report", PW / 2, 22, { align: "center" });

  pdf.setFontSize(8);
  pdf.setFont("helvetica", "normal");
  pdf.text(
    `${jobL.client_name || ""} · ${fmtDate(jobL.scheduled_date)} · Generated ${format(new Date(), "MMM dd, yyyy")}`,
    PW / 2, 30, { align: "center" }
  );

  // KPI strip
  const kpis = isDual
    ? [
        { v: String(jobL.total_panels_cleaned || "—"), l: "MORNING PANELS" },
        { v: String(jobR.total_panels_cleaned || "—"), l: "EVENING PANELS" },
        { v: String(parseInt(jobL.total_panels_cleaned || 0) + parseInt(jobR.total_panels_cleaned || 0)), l: "TOTAL PANELS" },
        { v: "2", l: "SESSIONS" },
      ]
    : [
        { v: String(jobL.total_panels_cleaned || "—"), l: "PANELS CLEANED" },
        { v: `${jobL.system_size || "—"} kW`,          l: "SYSTEM SIZE" },
        { v: jobL.team_lead_name || "N/A",             l: "TEAM LEAD" },
        { v: "COMPLETED",                               l: "STATUS" },
      ];

  const kpiW = (PW - 2 * M) / 4;
  const kpiY = 36;
  kpis.forEach(({ v, l }, i) => {
    const x = M + i * kpiW;
    if (i > 0) {
      pdf.setDrawColor(255, 255, 255);
      pdf.setLineWidth(0.4);
      pdf.line(x, kpiY, x, kpiY + 22);
    }
    pdf.setTextColor(...C.white);
    pdf.setFontSize(13);
    pdf.setFont("helvetica", "bold");
    pdf.text(truncate(v, kpiW - 4, 13), x + kpiW / 2, kpiY + 9, { align: "center" });
    pdf.setFontSize(6);
    pdf.setFont("helvetica", "normal");
    pdf.text(l, x + kpiW / 2, kpiY + 16, { align: "center" });
  });

  y = HH + 5;

  // ══════════════════════════════════════════════════════════════════
  // SHARED TEAM INFO BAR
  // ══════════════════════════════════════════════════════════════════
  const INFO_H = 20;
  pdf.setFillColor(...C.snow);
  pdf.roundedRect(M, y, PW - 2 * M, INFO_H, 2, 2, "F");
  pdf.setDrawColor(...C.silver);
  pdf.setLineWidth(0.3);
  pdf.roundedRect(M, y, PW - 2 * M, INFO_H, 2, 2, "S");

  const infoItems = [
    { l: "TEAM LEAD", v: jobL.team_lead_name || "N/A" },
    { l: "CONTACT",   v: jobL.contact_person || "N/A" },
    { l: "PHONE",     v: jobL.contact_number || "N/A" },
    { l: "LOCATION",  v: jobL.address        || "N/A" },
    { l: "SYSTEM",    v: `${jobL.system_size || "—"} kW` },
  ];

  const infoColW = (PW - 2 * M) / infoItems.length;
  infoItems.forEach(({ l, v }, i) => {
    const x = M + i * infoColW + 4;
    if (i > 0) {
      pdf.setDrawColor(...C.silver);
      pdf.setLineWidth(0.3);
      pdf.line(M + i * infoColW, y + 3, M + i * infoColW, y + INFO_H - 3);
    }
    pdf.setFontSize(6);
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(...C.gray);
    pdf.text(l, x, y + 7);
    pdf.setFontSize(7.5);
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(...C.dark);
    pdf.text(truncate(v, infoColW - 6, 7.5), x, y + 15);
  });

  if (isDual) {
    const badgeX = PW - M - 38;
    pdf.setFillColor(...C.purple);
    pdf.roundedRect(badgeX, y + 5, 36, 10, 2, 2, "F");
    pdf.setTextColor(...C.white);
    pdf.setFontSize(6);
    pdf.setFont("helvetica", "bold");
    pdf.text("SHARED TEAM - BOTH SESSIONS", badgeX + 18, y + 11.5, { align: "center" });
  }

  y += INFO_H + 6;

  // ══════════════════════════════════════════════════════════════════
  // COLUMN HEADERS
  // ══════════════════════════════════════════════════════════════════
  colJobs.forEach((job, ci) => {
    const cc  = colColors[ci];
    const x   = M + ci * (CW + 5);
    const lbl = isDual ? (ci === 0 ? "Morning Session" : "Evening Session") : "Site Details";

    pdf.setFillColor(...cc.bg);
    pdf.roundedRect(x, y, CW, 11, 2, 2, "F");
    pdf.setDrawColor(...cc.border);
    pdf.setLineWidth(0.5);
    pdf.roundedRect(x, y, CW, 11, 2, 2, "S");

    pdf.setFontSize(8);
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(...cc.accent);
    pdf.text(lbl, x + 4, y + 7.5);

    pdf.setFontSize(7);
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(...C.mid);
    pdf.text(truncate(job.site_name || "", CW - 40, 7), x + CW - 3, y + 7.5, { align: "right" });
  });
  y += 14;

  // ══════════════════════════════════════════════════════════════════
  // TIMELINE — side by side
  // ══════════════════════════════════════════════════════════════════
  const tlEvents = [
    { label: "Scheduled",    keys: ["scheduled_date"],                     color: C.blue,        isDate: true  },
    { label: "Site Arrival", keys: ["reached_location_at"],                color: C.amber,       isDate: false },
    { label: "Work Started", keys: ["started_at"],                         color: C.green,       isDate: false },
    { label: "Work Begun",   keys: ["before_photos_at","before_photo_at"], color: C.violet,      isDate: false },
    { label: "Work Done",    keys: ["after_photos_at","after_photo_at"],   color: [204,40,153],  isDate: false },
    { label: "Site Exit",    keys: ["completed_at"],                       color: C.green,       isDate: false },
  ];

  colJobs.forEach((_, ci) => {
    const x = M + ci * (CW + 5);
    pdf.setFontSize(6.5);
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(...C.gray);
    pdf.text("JOB TIMELINE", x + 2, y + 4);
  });
  y += 7;

  const ROW_H = 8;
  const maxTlRows = Math.max(...colJobs.map((job) =>
    tlEvents.filter((ev) => ev.keys.some((k) => job[k])).length
  ));

  colJobs.forEach((job, ci) => {
    const x  = M + ci * (CW + 5);
    let   ry = y;
    const evs = tlEvents.filter((ev) => ev.keys.some((k) => job[k]));

    evs.forEach((ev, idx) => {
      if (idx % 2 === 0) {
        pdf.setFillColor(...C.snow);
        pdf.rect(x, ry, CW, ROW_H, "F");
      }
      pdf.setFillColor(...ev.color);
      pdf.circle(x + 4, ry + ROW_H / 2, 1.8, "F");

      pdf.setFontSize(7);
      pdf.setFont("helvetica", "bold");
      pdf.setTextColor(...ev.color);
      pdf.text(ev.label, x + 8, ry + ROW_H / 2 + 1);

      const ts  = ev.keys.map((k) => job[k]).find(Boolean);
      const val = ev.isDate ? fmtDate(ts) : fmtTime(ts);
      pdf.setFont("helvetica", "normal");
      pdf.setTextColor(...C.dark);
      pdf.text(val, x + CW - 2, ry + ROW_H / 2 + 1, { align: "right" });
      ry += ROW_H;
    });
  });
  y += maxTlRows * ROW_H + 8;

  // ══════════════════════════════════════════════════════════════════
  // FSR DETAILS — side by side
  // ══════════════════════════════════════════════════════════════════
  if (colJobs.some((j) => !isMaint(j) && j.status === "completed")) {
    newPage(55);

    colJobs.forEach((_, ci) => {
      const x = M + ci * (CW + 5);
      pdf.setFontSize(6.5);
      pdf.setFont("helvetica", "bold");
      pdf.setTextColor(...C.gray);
      pdf.text("FIELD SERVICE REPORT", x + 2, y + 4);
    });
    y += 7;

    const fsrRows = [
      { label: "Cable Condition",   key: "cable_condition" },
      { label: "Panel Damage",      key: "panel_damage" },
      { label: "Inverter Alarm",    key: "inverter_alarm" },
      { label: "Potential Shading", key: "potential_shading" },
      { label: "Total Panels",      key: "total_panels_cleaned" },
    ];

    const FSR_H = 8;
    colJobs.forEach((job, ci) => {
      const x  = M + ci * (CW + 5);
      let   fy = y;
      fsrRows.forEach((f, idx) => {
        const raw    = (job[f.key] || "N/A").toString();
        const isGood = ["good", "no"].includes(raw.toLowerCase());
        const isBad  = ["yes", "bad"].includes(raw.toLowerCase());
        const valCol = isBad ? C.red : isGood ? C.green : C.dark;

        if (idx % 2 === 0) {
          pdf.setFillColor(...C.snow);
          pdf.rect(x, fy, CW, FSR_H, "F");
        }
        pdf.setFontSize(7);
        pdf.setFont("helvetica", "bold");
        pdf.setTextColor(...C.gray);
        pdf.text(f.label, x + 3, fy + FSR_H / 2 + 1);

        pdf.setFont("helvetica", "bold");
        pdf.setTextColor(...valCol);
        pdf.text(truncate(raw, CW / 2 - 4, 7), x + CW - 3, fy + FSR_H / 2 + 1, { align: "right" });
        fy += FSR_H;
      });
    });
    y += fsrRows.length * FSR_H + 8;
  }

  // ══════════════════════════════════════════════════════════════════
  // SIGNATURES
  // ══════════════════════════════════════════════════════════════════
  const sigJobs = colJobs.filter((j) => j.customer_signature);
  if (sigJobs.length > 0) {
    newPage(30);
    pdf.setFontSize(6.5);
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(...C.gray);
    pdf.text("CUSTOMER SIGNATURE", M + 2, y + 4);
    y += 7;
    for (const [ci, job] of sigJobs.entries()) {
      const x = isDual ? M + ci * (CW + 5) : M;
      const sigUrl = job.customer_signature.startsWith("http")
        ? job.customer_signature
        : `https://app.sowashusa.com/uploads/signatures/${job.customer_signature}`;
      await drawImg(sigUrl, x, y, CW * 0.6, 20);
    }
    y += 26;
  }

  // ══════════════════════════════════════════════════════════════════
  // PHOTOS
  // ══════════════════════════════════════════════════════════════════
  const PHOTO_COLS = 4;
  const GAP        = 3;
  const IMG_W      = (PW - 2 * M - GAP * (PHOTO_COLS - 1)) / PHOTO_COLS;
  const IMG_H      = IMG_W * 0.72;

  const drawPhotoGroup = async (groupLabel, accentColor, photoEntries) => {
    if (!photoEntries.length) return;
    newPage(IMG_H + 20);

    pdf.setFillColor(...accentColor);
    pdf.roundedRect(M, y, PW - 2 * M, 8, 2, 2, "F");
    pdf.setTextColor(...C.white);
    pdf.setFontSize(7.5);
    pdf.setFont("helvetica", "bold");
    pdf.text(groupLabel, M + 4, y + 5.5);
    y += 11;

    for (let i = 0; i < photoEntries.length; i++) {
      const col = i % PHOTO_COLS;
      if (col === 0 && i > 0) newPage(IMG_H + 10);

      const x = M + col * (IMG_W + GAP);

      pdf.setDrawColor(...C.silver);
      pdf.setLineWidth(0.3);
      pdf.rect(x, y, IMG_W, IMG_H);

      await drawImg(buildFullUrl(photoEntries[i]), x + 0.5, y + 0.5, IMG_W - 1, IMG_H - 1);

      pdf.setFontSize(5.5);
      pdf.setFont("helvetica", "normal");
      pdf.setTextColor(...C.gray);
      pdf.text(
        truncate(photoEntries[i].caption, IMG_W, 5.5),
        x + IMG_W / 2, y + IMG_H + 4, { align: "center" }
      );

      if (col === PHOTO_COLS - 1 || i === photoEntries.length - 1) {
        y += IMG_H + 8;
      }
    }
    y += 3;
  };

  const getEntries = (job, key, fallbackKey, labelPrefix) => {
    const basePath = isMaint(job)
      ? "https://app.sowashusa.com/uploads/maintenance"
      : "https://app.sowashusa.com/uploads/job-photos";
    let photos = parsePhotos(job[key]);
    if (!photos.length && fallbackKey && job[fallbackKey])
      photos = [job[fallbackKey]];
    return photos.map((url, pi) => ({
      url,
      basePath,
      caption: `${labelPrefix} ${pi + 1}`,
    }));
  };

  const photoGroups = isDual
    ? [
        ["MORNING — BEFORE PHOTOS", C.amber,  getEntries(jobL, "before_photos", "before_photo_url", "Before")],
        ["MORNING — AFTER PHOTOS",  C.green,  getEntries(jobL, "after_photos",  "after_photo_url",  "After")],
        ["MORNING — TOOL BOX TALK", C.violet, getEntries(jobL, "temp_voltage_photos", null, "TBT")],
        ["EVENING — BEFORE PHOTOS", C.amber,  getEntries(jobR, "before_photos", "before_photo_url", "Before")],
        ["EVENING — AFTER PHOTOS",  C.green,  getEntries(jobR, "after_photos",  "after_photo_url",  "After")],
        ["EVENING — TOOL BOX TALK", C.violet, getEntries(jobR, "temp_voltage_photos", null, "TBT")],
      ]
    : [
        ["BEFORE PHOTOS",        C.amber,  getEntries(jobL, "before_photos", "before_photo_url", "Before")],
        ["AFTER PHOTOS",         C.green,  getEntries(jobL, "after_photos",  "after_photo_url",  "After")],
        ["TOOL BOX TALK PHOTOS", C.violet, getEntries(jobL, "temp_voltage_photos", null, "TBT")],
      ];

  // Warm every photo in parallel BEFORE drawing, so the draw pass is just
  // cache hits instead of one serial network wait per image.
  await prefetchImgs(photoGroups.flatMap(([, , entries]) => entries.map(buildFullUrl)));

  for (const [label, color, entries] of photoGroups) {
    await drawPhotoGroup(label, color, entries);
  }

  // ══════════════════════════════════════════════════════════════════
  // REMARKS
  // ══════════════════════════════════════════════════════════════════
  colJobs.forEach((job) => {
    const txt = job.remarks || job.notes || job.additional_notes;
    if (!txt) return;
    newPage(22);
    const lines = pdf.splitTextToSize(txt, PW - 2 * M - 8);
    const bH    = lines.length * 5 + 10;
    pdf.setFillColor(239, 246, 255);
    pdf.roundedRect(M, y, PW - 2 * M, bH, 2, 2, "F");
    pdf.setDrawColor(...C.blue);
    pdf.setLineWidth(0.4);
    pdf.roundedRect(M, y, PW - 2 * M, bH, 2, 2, "S");
    pdf.setFontSize(7);
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(...C.blue);
    pdf.text("REMARKS", M + 3, y + 6);
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(...C.dark);
    pdf.text(lines, M + 3, y + 12);
    y += bH + 5;
  });

  // ══════════════════════════════════════════════════════════════════
  // PAGE FOOTERS
  // ══════════════════════════════════════════════════════════════════
  const totalPages = pdf.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    pdf.setPage(i);
    pdf.setDrawColor(...C.silver);
    pdf.setLineWidth(0.3);
    pdf.line(M, PH - 10, PW - M, PH - 10);
    pdf.setFontSize(6.5);
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(...C.gray);
    pdf.text("SoWash — Maintenance Services", M, PH - 6);
    pdf.text(`Page ${i} of ${totalPages}`, PW / 2, PH - 6, { align: "center" });
    pdf.text(format(new Date(), "MMM dd, yyyy"), PW - M, PH - 6, { align: "right" });
  }

  pdf.save(
    `Report_${(jobArg.site_name || String(jobArg.schedule_id)).replace(/[^a-zA-Z0-9]/g, "_")}_${selDateStr}.pdf`
  );
};

  // Single-job download (called from modal)
  const handleDownloadReport = async () => {
    setDownloadLoading(true);
    try {
      await generateAndDownloadPDF(selectedJob);
      showToast('success', 'Report downloaded successfully');
    } catch (error) {
      console.error('PDF Error:', error);
      showToast('error', 'Failed to generate report: ' + error.message);
    } finally {
      setDownloadLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "completed":
        return { bg: "#ECFDF5", border: "#10B981", text: "#065F46" };
      case "started":
      case "in_progress":
      case "before_photos":
      case "after_photos":
        return { bg: "#FEF3C7", border: "#F59E0B", text: "#92400E" };
      case "scheduled":
        return { bg: "#EFF6FF", border: "#3B82F6", text: "#1E40AF" };
      default:
        return { bg: "#F3F4F6", border: "#9CA3AF", text: "#374151" };
    }
  };

  const getStatusIcon = (status) =>
    status === "completed"
      ? "✓"
      : ["started", "in_progress", "before_photos", "after_photos"].includes(
          status,
        )
        ? "⏳"
        : "📅";

  const getJobTypeColor = (type) =>
    type === "maintenance"
      ? { bg: "#FDF4FF", border: "#C026D3", text: "#86198F" }
      : { bg: "#DBEAFE", border: "#2563EB", text: "#1E40AF" };

  if (loading && jobs.length === 0)
    return <div className="p-5 text-center">Loading...</div>;

  return (
    <div
      style={{ minHeight: "100vh", background: "#F8F9FA", padding: "24px" }}
    >
      {/* Toast notification */}
      {toast && (
        <div style={{ position:'fixed', bottom:'32px', left:'50%', transform:'translateX(-50%)',
          zIndex:99999, display:'flex', alignItems:'center', gap:'10px',
          background: toast.type==='success' ? '#10B981' : '#EF4444',
          color:'white', padding:'14px 24px', borderRadius:'14px',
          boxShadow:'0 8px 32px rgba(0,0,0,0.18)', fontSize:'15px', fontWeight:'600',
          minWidth:'260px', maxWidth:'90vw', animation:'slideUp 0.3s ease-out' }}>
          <span style={{ fontSize:'20px' }}>{toast.type==='success' ? '✅' : '❌'}</span>
          {toast.message}
        </div>
      )}
      {/* Uploading overlay */}
      {uploading && (
        <div style={{ position:'fixed', inset:0, zIndex:99998, background:'rgba(0,0,0,0.35)',
          display:'flex', alignItems:'center', justifyContent:'center' }}>
          <div style={{ background:'white', borderRadius:'16px', padding:'32px 40px',
            display:'flex', flexDirection:'column', alignItems:'center', gap:'16px',
            boxShadow:'0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ width:'40px', height:'40px', border:'4px solid #E9ECEF',
              borderTop:'4px solid #667EEA', borderRadius:'50%', animation:'spin 0.8s linear infinite' }} />
            <span style={{ fontSize:'15px', fontWeight:'600', color:'#374151' }}>Uploading photo...</span>
          </div>
        </div>
      )}
      <style>{`
                @keyframes spin { to { transform: rotate(360deg); } }
                @keyframes fadeIn { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
                @keyframes slideUp { from { opacity: 0; transform: translateY(30px) scale(0.95); } to { opacity: 1; transform: translateY(0) scale(1); } }
                .job-card { animation: fadeIn 0.5s ease-out; transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1); }
                .job-card:hover { transform: translateY(-8px); box-shadow: 0 20px 40px rgba(0,0,0,0.15) !important; }
                .stat-card { transition: all 0.3s ease; cursor: pointer; }
                .stat-card:hover { transform: translateY(-4px); box-shadow: 0 12px 24px rgba(0,0,0,0.1) !important; }
                .filter-btn { transition: all 0.2s ease; border: 2px solid transparent; }
                .filter-btn.active { border-color: #667EEA !important; box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.1); }
                .nav-card { background: white; border-radius: 16px; padding: 24px; box-shadow: 0 4px 12px rgba(0,0,0,0.06); border: 1px solid #E5E7EB; transition: all 0.2s; cursor: pointer; display: flex; align-items: center; gap: 16px; animation: slideUp 0.3s ease-out; }
                .nav-card:hover { transform: translateY(-3px); box-shadow: 0 8px 20px rgba(0,0,0,0.08); border-color: #667EEA; }
            `}</style>

      {/* Header */}
      <div
        style={{
          background: "linear-gradient(135deg,#6528a9, #cc2899)",
          borderRadius: "20px",
          padding: "40px",
          marginBottom: "32px",
          boxShadow: "0 10px 30px rgba(102, 126, 234, 0.3)",
          animation: "slideUp 0.5s ease-out",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "20px",
          }}
        >
          <div>
            <h1
              style={{
                fontSize: "36px",
                fontWeight: "800",
                color: "white",
                margin: 0,
                marginBottom: "8px",
                letterSpacing: "-0.5px",
                display: "flex",
                alignItems: "center",
                gap: "12px",
              }}
            >
              <TimeIcon style={{ fontSize: "40px" }} /> Job History & Analytics
            </h1>
            <p
              style={{
                color: "rgba(255,255,255,0.9)",
                margin: 0,
                fontSize: "16px",
                fontWeight: "400",
              }}
            >
              Track cleaning jobs, maintenance services, and performance metrics
            </p>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: "20px",
          marginBottom: "32px",
        }}
      >
        <div
          className="stat-card"
          style={{
            background: "linear-gradient(90deg, #6528a9, #cc2899)",
            padding: "28px",
            borderRadius: "16px",
            boxShadow: "0 8px 20px rgba(102, 126, 234, 0.25)",
            color: "white",
          }}
        >
          <div
            style={{
              fontSize: "14px",
              opacity: 0.9,
              marginBottom: "8px",
              fontWeight: "600",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
            }}
          >
            Total Jobs
          </div>
          <div
            style={{
              fontSize: "42px",
              fontWeight: "800",
              letterSpacing: "-1px",
            }}
          >
            {stats.total}
          </div>
          <div
            style={{
              fontSize: "13px",
              opacity: 0.85,
              marginTop: "8px",
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <TrendingUpIcon style={{ fontSize: "16px" }} /> All time
          </div>
        </div>
        <div
          className="stat-card"
          style={{
            background: "white",
            padding: "28px",
            borderRadius: "16px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
            border: "2px solid #E9ECEF",
          }}
        >
          <div
            style={{
              fontSize: "14px",
              color: "#6C757D",
              marginBottom: "8px",
              fontWeight: "600",
              textTransform: "uppercase",
            }}
          >
            Completed
          </div>
          <div
            style={{
              fontSize: "42px",
              fontWeight: "800",
              color: "#10B981",
              letterSpacing: "-1px",
            }}
          >
            {stats.completed}
          </div>
          <div
            style={{
              fontSize: "13px",
              color: "#6C757D",
              marginTop: "8px",
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <CheckCircleIcon style={{ fontSize: "16px" }} /> Jobs
          </div>
        </div>
        <div
          className="stat-card"
          style={{
            background: "white",
            padding: "28px",
            borderRadius: "16px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
            border: "2px solid #E9ECEF",
          }}
        >
          <div
            style={{
              fontSize: "14px",
              color: "#6C757D",
              marginBottom: "8px",
              fontWeight: "600",
              textTransform: "uppercase",
            }}
          >
            In Progress
          </div>
          <div
            style={{
              fontSize: "42px",
              fontWeight: "800",
              color: "#F59E0B",
              letterSpacing: "-1px",
            }}
          >
            {stats.inProgress}
          </div>
          <div
            style={{
              fontSize: "13px",
              color: "#6C757D",
              marginTop: "8px",
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <ScheduleIcon style={{ fontSize: "16px" }} /> Active
          </div>
        </div>
        <div
          className="stat-card"
          style={{
            background: "white",
            padding: "28px",
            borderRadius: "16px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
            border: "2px solid #E9ECEF",
          }}
        >
          <div
            style={{
              fontSize: "14px",
              color: "#6C757D",
              marginBottom: "8px",
              fontWeight: "600",
              textTransform: "uppercase",
            }}
          >
            Scheduled
          </div>
          <div
            style={{
              fontSize: "42px",
              fontWeight: "800",
              color: "#3B82F6",
              letterSpacing: "-1px",
            }}
          >
            {stats.scheduled}
          </div>
          <div
            style={{
              fontSize: "13px",
              color: "#6C757D",
              marginTop: "8px",
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <CalendarIcon style={{ fontSize: "16px" }} /> Upcoming
          </div>
        </div>
      </div>

      {/* Search and Filter Bar */}
      <div
        style={{
          background: "white",
          padding: "24px",
          borderRadius: "16px",
          marginBottom: "24px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
          border: "1px solid #E9ECEF",
        }}
      >
        <div
          style={{
            display: "flex",
            gap: "16px",
            flexWrap: "wrap",
            alignItems: "center",
            marginBottom: "16px",
          }}
        >
          <div style={{ flex: "1", minWidth: "280px", position: "relative" }}>
            <SearchIcon
              style={{
                position: "absolute",
                left: "16px",
                top: "50%",
                transform: "translateY(-50%)",
                color: "#9CA3AF",
                fontSize: "20px",
              }}
            />
            <input
              type="text"
              placeholder="Search by site, client, or location..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: "100%",
                padding: "14px 16px 14px 48px",
                border: "2px solid #E5E7EB",
                borderRadius: "12px",
                fontSize: "15px",
                fontWeight: "500",
                outline: "none",
                transition: "all 0.2s",
              }}
              onFocus={(e) => {
                e.target.style.borderColor = "#667EEA";
                e.target.style.boxShadow = "0 0 0 4px rgba(102, 126, 234, 0.1)";
              }}
              onBlur={(e) => {
                e.target.style.borderColor = "#E5E7EB";
                e.target.style.boxShadow = "none";
              }}
            />
          </div>
          <div style={{ display: "flex", gap: "10px" }}>
            <button
              onClick={() => setFilterType("all")}
              className={`filter-btn ${filterType === "all" ? "active" : ""}`}
              style={{
                padding: "10px 20px",
                background: filterType === "all" ? "#F3F4F6" : "white",
                color: "#6B7280",
                borderRadius: "10px",
                fontWeight: "600",
                border: "1px solid #E5E7EB",
                cursor: "pointer",
              }}
            >
              All Types
            </button>
            <button
              onClick={() => setFilterType("commercial")}
              className={`filter-btn ${filterType === "commercial" ? "active" : ""}`}
              style={{
                padding: "10px 20px",
                background: filterType === "commercial" ? "#EFF6FF" : "white",
                color: "#2563EB",
                borderRadius: "10px",
                fontWeight: "600",
                border: "1px solid #E5E7EB",
                cursor: "pointer",
              }}
            >
              Commercial
            </button>
            <button
              onClick={() => setFilterType("maintenance")}
              className={`filter-btn ${filterType === "maintenance" ? "active" : ""}`}
              style={{
                padding: "10px 20px",
                background: filterType === "maintenance" ? "#FDF4FF" : "white",
                color: "#C026D3",
                borderRadius: "10px",
                fontWeight: "600",
                border: "1px solid #E5E7EB",
                cursor: "pointer",
              }}
            >
              Maintenance
            </button>
          </div>
        </div>
        <div
          style={{
            display: "flex",
            gap: "10px",
            flexWrap: "wrap",
            borderTop: "1px solid #E5E7EB",
            paddingTop: "16px",
          }}
        >
          <button
            onClick={() => setFilterStatus("all")}
            style={{
              padding: "10px 20px",
              background: filterStatus === "all" ? "#F3F4F6" : "white",
              color: "#4B5563",
              borderRadius: "10px",
              fontWeight: "600",
              border: "1px solid #E5E7EB",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <AssignmentIcon style={{ fontSize: "16px" }} /> All Status
          </button>
          <button
            onClick={() => setFilterStatus("completed")}
            style={{
              padding: "10px 20px",
              background: filterStatus === "completed" ? "#ECFDF5" : "white",
              color: "#059669",
              borderRadius: "10px",
              fontWeight: "600",
              border: "1px solid #E5E7EB",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <CheckCircleIcon style={{ fontSize: "16px" }} /> Completed
          </button>
          <button
            onClick={() => setFilterStatus("started")}
            style={{
              padding: "10px 20px",
              background: filterStatus === "started" ? "#FFFBEB" : "white",
              color: "#D97706",
              borderRadius: "10px",
              fontWeight: "600",
              border: "1px solid #E5E7EB",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <ScheduleIcon style={{ fontSize: "16px" }} /> In Progress
          </button>
          <button
            onClick={() => setFilterStatus("scheduled")}
            style={{
              padding: "10px 20px",
              background: filterStatus === "scheduled" ? "#EFF6FF" : "white",
              color: "#2563EB",
              borderRadius: "10px",
              fontWeight: "600",
              border: "1px solid #E5E7EB",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <CalendarIcon style={{ fontSize: "16px" }} /> Scheduled
          </button>
        </div>

        {/* APPROVAL FILTER ROW — only relevant for completed jobs */}
        {(filterStatus === "completed" || filterStatus === "all") && (
          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", borderTop: "1px solid #F3F4F6", paddingTop: "14px" }}>
            <span style={{ fontSize: "12px", fontWeight: "700", color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.5px", display: "flex", alignItems: "center", paddingRight: "4px" }}>
              Approval
            </span>
            {[
              { key: "all",      label: "All",      color: "#4B5563", activeBg: "#F3F4F6", icon: null },
              { key: "pending",  label: "Pending",  color: "#D97706", activeBg: "#FFFBEB", icon: <PendingIcon style={{ fontSize: "15px" }} /> },
              { key: "approved", label: "Approved", color: "#059669", activeBg: "#ECFDF5", icon: <ApproveIcon style={{ fontSize: "15px" }} /> },
              { key: "rejected", label: "Rejected", color: "#DC2626", activeBg: "#FEF2F2", icon: <RejectIcon style={{ fontSize: "15px" }} /> },
            ].map(({ key, label, color, activeBg, icon }) => (
              <button
                key={key}
                onClick={() => setFilterApproval(key)}
                style={{
                  padding: "7px 16px",
                  background: filterApproval === key ? activeBg : "white",
                  color,
                  borderRadius: "8px",
                  fontWeight: "600",
                  fontSize: "13px",
                  border: filterApproval === key ? `1.5px solid ${color}` : "1px solid #E5E7EB",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                  transition: "all 0.15s",
                }}
              >
                {icon} {label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* BROWSE MODE TOGGLE */}
      <div
        style={{
          background: "white", borderRadius: "16px", padding: "14px 20px",
          marginBottom: "20px", boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
          border: "1px solid #E9ECEF", display: "flex", alignItems: "center", gap: "12px",
        }}
      >
        <span style={{ fontSize: "13px", fontWeight: "700", color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.5px" }}>
          Browse by
        </span>
        <div style={{ display: "flex", background: "#F3F4F6", borderRadius: "12px", padding: "4px", gap: "4px" }}>
          {[["byClient", "🏢  By Client"], ["byDate", "📅  By Date"]].map(([mode, label]) => (
            <button
              key={mode}
              onClick={() => {
                setBrowseMode(mode);
                setSelectedDate(null);
                setBulkDownloadProgress({});
                setBulkDone(false);
                if (mode === "byClient") setViewMode("clients");
              }}
              style={{
                padding: "9px 20px", borderRadius: "9px", border: "none",
                fontWeight: "700", fontSize: "14px", cursor: "pointer",
                background: browseMode === mode ? "white" : "transparent",
                color: browseMode === mode ? "#111827" : "#6B7280",
                boxShadow: browseMode === mode ? "0 2px 8px rgba(0,0,0,0.1)" : "none",
                transition: "all 0.2s",
              }}
            >{label}</button>
          ))}
        </div>
      </div>

      {/* BREADCRUMB & BACK BUTTON */}
      <div style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: "24px" }}>
        {/* Back button — shown when not at the top level of either mode */}
        {(browseMode === "byClient" ? viewMode !== "clients" : selectedDate !== null) && (
          <button
            onClick={handleBack}
            style={{
              display: "flex", alignItems: "center", gap: "6px",
              padding: "10px 20px", background: "white",
              border: "1px solid #E5E7EB", borderRadius: "10px",
              cursor: "pointer", fontWeight: "600", color: "#374151",
              boxShadow: "0 2px 5px rgba(0,0,0,0.05)",
            }}
          >
            <ArrowBackIcon style={{ fontSize: "18px" }} /> Back
          </button>
        )}

        <div style={{ fontSize: "18px", fontWeight: "700", color: "#111827", display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
          {browseMode === "byDate" ? (
            // By-Date breadcrumb: All Dates / Apr 22, 2026
            <>
              <span
                style={{ color: selectedDate ? "#6B7280" : "#111827", cursor: selectedDate ? "pointer" : "default" }}
                onClick={() => { if (selectedDate) { setSelectedDate(null); setBulkDownloadProgress({}); setBulkDone(false); } }}
              >
                All Dates
              </span>
              {selectedDate && (
                <>
                  <span>/</span>
                  <span>{format(new Date(selectedDate), "MMMM dd, yyyy")}</span>
                </>
              )}
            </>
          ) : (
            // By-Client breadcrumb: All Clients / Client / Month / Date
            <>
              <span
                style={{ color: viewMode === "clients" ? "#111827" : "#6B7280", cursor: viewMode !== "clients" ? "pointer" : "default" }}
                onClick={() => {
                  if (viewMode !== "clients") {
                    setViewMode("clients"); setSelectedClient(null); setSelectedMonth(null); setSearchQuery("");
                  }
                }}
              >
                All Clients
              </span>
              {viewMode !== "clients" && (
                <>
                  <span>/</span>
                  <span
                    style={{ color: viewMode === "months" ? "#111827" : "#6B7280", cursor: viewMode !== "months" ? "pointer" : "default" }}
                    onClick={() => { if (viewMode !== "months") { setViewMode("months"); setSelectedMonth(null); } }}
                  >
                    {selectedClient}
                  </span>
                </>
              )}
              {(viewMode === "dates" || viewMode === "jobs") && (
                <>
                  <span>/</span>
                  <span
                    style={{ color: viewMode === "dates" ? "#111827" : "#6B7280", cursor: viewMode === "jobs" ? "pointer" : "default" }}
                    onClick={() => { if (viewMode === "jobs") { setViewMode("dates"); setSelectedDate(null); } }}
                  >
                    {selectedMonth && format(new Date(selectedMonth + "-01"), "MMMM yyyy")}
                  </span>
                </>
              )}
              {browseMode === "byClient" && viewMode === "jobs" && (
                <>
                  <span>/</span>
                  <span>{format(new Date(selectedDate), "MMM dd")}</span>
                </>
              )}
            </>
          )}
        </div>
      </div>


      {/* VIEW 1: CLIENT CARDS */}
      {browseMode === "byClient" && viewMode === "clients" && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
            gap: "24px",
          }}
        >
          {Object.keys(clientGroups).length > 0 ? (
            Object.keys(clientGroups).map((client, i) => (
              <div
                key={i}
                className="nav-card"
                onClick={() => handleClientClick(client)}
              >
                <div
                  style={{
                    width: "56px",
                    height: "56px",
                    borderRadius: "50%",
                    background: "#F0F4FF",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <BusinessIcon
                    style={{ color: "#3B82F6", fontSize: "28px" }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <h4
                    style={{
                      fontSize: "16px",
                      fontWeight: "700",
                      color: "#111827",
                      margin: 0,
                    }}
                  >
                    {client}
                  </h4>
                  <p
                    style={{
                      fontSize: "13px",
                      color: "#6B7280",
                      margin: "4px 0 0 0",
                    }}
                  >
                    {clientGroups[client].length} Total Jobs
                  </p>
                </div>
                <div
                  style={{
                    background: "#F3F4F6",
                    padding: "8px",
                    borderRadius: "50%",
                  }}
                >
                  <ArrowRightIcon style={{ color: "#9CA3AF" }} />
                </div>
              </div>
            ))
          ) : (
            <div
              style={{
                gridColumn: "1/-1",
                textAlign: "center",
                color: "#6B7280",
              }}
            >
              No clients found.
            </div>
          )}
        </div>
      )}

      {/* VIEW 2: MONTH CARDS */}
      {browseMode === "byClient" && viewMode === "months" && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
            gap: "24px",
          }}
        >
          {Object.keys(monthGroups)
            .sort((a, b) => new Date(b) - new Date(a))
            .map((monthKey, i) => (
              <div
                key={i}
                className="nav-card"
                onClick={() => handleMonthClick(monthKey)}
              >
                <div
                  style={{
                    width: "56px",
                    height: "56px",
                    borderRadius: "50%",
                    background: "#FDF4FF",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <EventIcon style={{ color: "#C026D3", fontSize: "28px" }} />
                </div>
                <div style={{ flex: 1 }}>
                  <h4
                    style={{
                      fontSize: "16px",
                      fontWeight: "700",
                      color: "#111827",
                      margin: 0,
                    }}
                  >
                    {format(new Date(monthKey + "-01"), "MMMM yyyy")}
                  </h4>
                  <p
                    style={{
                      fontSize: "13px",
                      color: "#6B7280",
                      margin: "4px 0 0 0",
                    }}
                  >
                    {monthGroups[monthKey].length} Jobs
                  </p>
                </div>
                <div
                  style={{
                    background: "#F3F4F6",
                    padding: "8px",
                    borderRadius: "50%",
                  }}
                >
                  <ArrowRightIcon style={{ color: "#9CA3AF" }} />
                </div>
              </div>
            ))}
        </div>
      )}

      {/* VIEW 3: DATE CARDS */}
      {browseMode === "byClient" && viewMode === "dates" && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
            gap: "24px",
          }}
        >
          {Object.keys(dateGroups)
            .sort((a, b) => new Date(b) - new Date(a))
            .map((date, i) => (
              <div
                key={i}
                className="nav-card"
                onClick={() => handleDateClick(date)}
              >
                <div
                  style={{
                    width: "56px",
                    height: "56px",
                    borderRadius: "50%",
                    background: "#FFFBEB",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <DateRangeIcon
                    style={{ color: "#F59E0B", fontSize: "28px" }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <h4
                    style={{
                      fontSize: "16px",
                      fontWeight: "700",
                      color: "#111827",
                      margin: 0,
                    }}
                  >
                    {format(new Date(date), "MMMM dd, yyyy")}
                  </h4>
                  <p
                    style={{
                      fontSize: "13px",
                      color: "#6B7280",
                      margin: "4px 0 0 0",
                    }}
                  >
                    {dateGroups[date].length} Jobs •{" "}
                    {format(new Date(date), "EEEE")}
                  </p>
                </div>
                <div
                  style={{
                    background: "#F3F4F6",
                    padding: "8px",
                    borderRadius: "50%",
                  }}
                >
                  <ArrowRightIcon style={{ color: "#9CA3AF" }} />
                </div>
              </div>
            ))}
        </div>
      )}

      {/* BY-DATE VIEW A: All dates list */}
      {browseMode === "byDate" && !selectedDate && (
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          {Object.keys(allDateGroups).sort((a, b) => new Date(b) - new Date(a)).map((dateStr) => {
            const dayJobs = allDateGroups[dateStr];
            const completed  = dayJobs.filter(j => j.status === "completed").length;
            const inProgress = dayJobs.filter(j => ["started","in_progress","before_photos","after_photos"].includes(j.status)).length;
            const scheduled  = dayJobs.filter(j => j.status === "scheduled").length;
            const uniqueClients = [...new Set(dayJobs.map(j => j.client_name).filter(Boolean))];
            return (
              <div
                key={dateStr}
                className="nav-card"
                onClick={() => { setSelectedDate(dateStr); setBulkDownloadProgress({}); setBulkDone(false); }}
                style={{ display: "flex", alignItems: "center", gap: "20px", padding: "20px 24px" }}
              >
                {/* Date tile */}
                <div style={{
                  minWidth: "64px", borderRadius: "14px", padding: "10px", textAlign: "center",
                  background: dateStr === format(new Date(), "yyyy-MM-dd") ? "linear-gradient(135deg,#6528a9,#cc2899)" : "#F8F9FA",
                  border: dateStr === format(new Date(), "yyyy-MM-dd") ? "none" : "1px solid #E5E7EB",
                  flexShrink: 0,
                }}>
                  <div style={{ fontSize: "11px", fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.5px", color: dateStr === format(new Date(), "yyyy-MM-dd") ? "rgba(255,255,255,0.8)" : "#9CA3AF" }}>
                    {format(new Date(dateStr), "EEE")}
                  </div>
                  <div style={{ fontSize: "26px", fontWeight: "900", lineHeight: 1.1, color: dateStr === format(new Date(), "yyyy-MM-dd") ? "white" : "#111827" }}>
                    {format(new Date(dateStr), "dd")}
                  </div>
                  <div style={{ fontSize: "11px", fontWeight: "600", color: dateStr === format(new Date(), "yyyy-MM-dd") ? "rgba(255,255,255,0.75)" : "#6B7280" }}>
                    {format(new Date(dateStr), "MMM yyyy")}
                  </div>
                </div>
                {/* Info */}
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px" }}>
                    <span style={{ fontSize: "16px", fontWeight: "700", color: "#111827" }}>
                      {dayJobs.length} Job{dayJobs.length !== 1 ? "s" : ""}
                    </span>
                    {dateStr === format(new Date(), "yyyy-MM-dd") && (
                      <span style={{ fontSize: "11px", fontWeight: "700", background: "linear-gradient(135deg,#6528a9,#cc2899)", color: "white", padding: "2px 10px", borderRadius: "20px" }}>Today</span>
                    )}
                  </div>
                  <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "8px" }}>
                    {completed  > 0 && <span style={{ fontSize: "12px", fontWeight: "600", color: "#10B981", background: "#ECFDF5", padding: "3px 10px", borderRadius: "20px" }}>✓ {completed} Done</span>}
                    {inProgress > 0 && <span style={{ fontSize: "12px", fontWeight: "600", color: "#F59E0B", background: "#FFFBEB", padding: "3px 10px", borderRadius: "20px" }}>⏳ {inProgress} In Progress</span>}
                    {scheduled  > 0 && <span style={{ fontSize: "12px", fontWeight: "600", color: "#3B82F6", background: "#EFF6FF", padding: "3px 10px", borderRadius: "20px" }}>📋 {scheduled} Scheduled</span>}
                  </div>
                  <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                    {uniqueClients.map(client => (
                      <span key={client} style={{ fontSize: "11px", fontWeight: "600", color: "#6B7280", background: "#F3F4F6", padding: "2px 8px", borderRadius: "6px" }}>{client}</span>
                    ))}
                  </div>
                </div>
                <div style={{ background: "#F3F4F6", padding: "8px", borderRadius: "50%" }}>
                  <ArrowRightIcon style={{ color: "#9CA3AF" }} />
                </div>
              </div>
            );
          })}
          {Object.keys(allDateGroups).length === 0 && (
            <div style={{ textAlign: "center", padding: "40px", color: "#6B7280" }}>No jobs found.</div>
          )}
        </div>
      )}

      {/* BY-DATE VIEW B: Jobs for selected date + Download All */}
      {browseMode === "byDate" && selectedDate && (() => {
        const dayJobs = allDateGroups[selectedDate] || [];
        const doneCount  = Object.values(bulkDownloadProgress).filter(v => v === "done").length;
        const totalCount = dayJobs.length;
        return (
          <div>
            {/* Download All Banner */}
            <div style={{
              background: "white", borderRadius: "16px", padding: "20px 24px", marginBottom: "16px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
              border: bulkDone ? "1px solid #10B981" : "1px solid #E5E7EB",
              transition: "border 0.3s",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: "700", fontSize: "15px", color: "#111827" }}>
                    {totalCount} Report{totalCount !== 1 ? "s" : ""} · {format(new Date(selectedDate), "MMMM dd, yyyy")}
                  </div>
                  <div style={{ fontSize: "13px", color: "#6B7280", marginTop: "2px" }}>
                    Each job downloads as a separate PDF file
                  </div>
                </div>
                <button
                  onClick={handleDownloadAllReports}
                  disabled={bulkDownloading}
                  style={{
                    padding: "12px 22px",
                    background: bulkDownloading ? "#F3F4F6" : "linear-gradient(135deg,#6528a9,#cc2899)",
                    color: bulkDownloading ? "#9CA3AF" : "white",
                    border: "none", borderRadius: "12px", fontSize: "14px", fontWeight: "700",
                    cursor: bulkDownloading ? "not-allowed" : "pointer",
                    display: "flex", alignItems: "center", gap: "8px",
                    whiteSpace: "nowrap", transition: "all 0.2s",
                    boxShadow: bulkDownloading ? "none" : "0 4px 14px rgba(101,40,169,0.35)",
                  }}
                >
                  {bulkDownloading ? (
                    <>
                      <span style={{ width:"15px", height:"15px", border:"2px solid #D1D5DB", borderTop:"2px solid #9CA3AF", borderRadius:"50%", display:"inline-block", animation:"spin 0.8s linear infinite" }} />
                      Downloading…
                    </>
                  ) : (
                    <><DownloadIcon style={{ fontSize: "18px" }} /> Download All Reports</>
                  )}
                </button>
              </div>
              {/* Progress bar */}
              {(bulkDownloading || bulkDone) && (
                <div style={{ marginTop: "16px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
                    <span style={{ fontSize: "13px", fontWeight: "700", color: bulkDone ? "#10B981" : "#6528a9" }}>
                      {bulkDone ? "✅ All reports downloaded!" : `Generating PDF ${doneCount + 1} of ${totalCount}…`}
                    </span>
                    <span style={{ fontSize: "13px", color: "#6B7280", fontWeight: "600" }}>{doneCount} / {totalCount}</span>
                  </div>
                  <div style={{ height: "8px", background: "#F3F4F6", borderRadius: "99px", overflow: "hidden" }}>
                    <div style={{
                      height: "100%", borderRadius: "99px", transition: "width 0.4s ease",
                      width: `${totalCount > 0 ? (doneCount / totalCount) * 100 : 0}%`,
                      background: bulkDone ? "#10B981" : "linear-gradient(90deg,#6528a9,#cc2899)",
                    }} />
                  </div>
                </div>
              )}
            </div>

            {/* Job cards */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(380px, 1fr))", gap: "20px" }}>
              {dayJobs.map((job, index) => {
                const statusColors = getStatusColor(job.status);
                const typeColors   = getJobTypeColor(job.job_type);
                const prog         = bulkDownloadProgress[job.schedule_id];
                return (
                  <div
                    key={job.unique_id || job.schedule_id || index}
                    className="job-card"
                    style={{
                      background: "white", borderRadius: "20px", padding: "24px",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                      border: prog === "done" ? "2px solid #10B981" : `2px solid ${typeColors.border}`,
                      position: "relative", overflow: "hidden",
                      animationDelay: `${index * 0.08}s`, transition: "border 0.3s",
                    }}
                  >
                    {/* Type badge */}
                    <div style={{
                      position: "absolute", top: "14px", right: "14px",
                      padding: "5px 10px", background: typeColors.bg, color: typeColors.text,
                      borderRadius: "8px", fontSize: "11px", fontWeight: "700",
                      textTransform: "uppercase", border: `1px solid ${typeColors.border}`,
                      display: "flex", alignItems: "center", gap: "4px", zIndex: 2,
                    }}>
                      {job.job_type === "maintenance" ? <><BuildIcon style={{ fontSize:"13px" }} /> Maintenance</> : <><CleaningIcon style={{ fontSize:"13px" }} /> Commercial</>}
                    </div>
                    {/* Site + client */}
                    <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px", paddingRight: "80px" }}>
                      <div style={{ width:"48px", height:"48px", borderRadius:"12px", background: statusColors.bg, border:`2px solid ${statusColors.border}`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:"20px", fontWeight:"700", color: statusColors.text, flexShrink:0 }}>
                        {(job.site_name || "?").charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div style={{ fontSize:"16px", fontWeight:"700", color:"#111827", marginBottom:"2px" }}>{job.site_name}</div>
                        <div style={{ fontSize:"13px", color:"#6B7280" }}>{job.client_name}</div>
                      </div>
                    </div>
                    {/* Status */}
                    <div style={{ display:"flex", alignItems:"center", gap:"8px", marginBottom:"10px" }}>
                      <div style={{ width:"8px", height:"8px", borderRadius:"50%", background: statusColors.border }} />
                      <span style={{ fontSize:"13px", fontWeight:"600", color: statusColors.text, textTransform:"capitalize" }}>{job.status?.replace(/_/g," ")}</span>
                    </div>
                    {/* Approval badge — completed jobs only */}
                    {job.status === "completed" && (() => {
                      const ab = getApprovalBadge(job.approval_status);
                      return (
                        <div style={{ marginBottom:"12px" }}>
                          <span style={{ fontSize:"12px", fontWeight:"700", color: ab.color, background: ab.bg, border:`1px solid ${ab.border}`, padding:"3px 12px", borderRadius:"20px", display:"inline-flex", alignItems:"center", gap:"5px" }}>
                            {ab.icon} {ab.label}
                          </span>
                        </div>
                      );
                    })()}
                    {/* Per-job bulk status indicator */}
                    {prog && (
                      <div style={{ marginBottom:"12px", padding:"8px 12px", borderRadius:"10px", background: prog==="done" ? "#ECFDF5" : prog==="error" ? "#FEF2F2" : "#F5F3FF", display:"flex", alignItems:"center", gap:"8px" }}>
                        {prog === "downloading" && <span style={{ width:"13px", height:"13px", border:"2px solid #e0c9f7", borderTop:"2px solid #6528a9", borderRadius:"50%", display:"inline-block", animation:"spin 0.8s linear infinite" }} />}
                        <span style={{ fontSize:"12px", fontWeight:"700", color: prog==="done" ? "#10B981" : prog==="error" ? "#EF4444" : "#6528a9" }}>
                          {prog==="pending" ? "⏳ Queued" : prog==="downloading" ? "Generating PDF…" : prog==="done" ? "✅ Downloaded" : "❌ Failed"}
                        </span>
                      </div>
                    )}
                    {/* Action buttons */}
                    <div style={{ display:"flex", gap:"10px" }}>
                      <button
                        onClick={() => handleViewDetails(job)}
                        style={{ flex:1, padding:"11px", background: job.job_type==="maintenance" ? "linear-gradient(135deg,#C026D3,#86198F)" : "linear-gradient(135deg,#667EEA,#764BA2)", color:"white", border:"none", borderRadius:"10px", fontSize:"14px", fontWeight:"700", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:"6px" }}
                      >
                        <ViewIcon style={{ fontSize:"16px" }} /> View Details
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* VIEW 4: JOBS GRID */}
      {viewMode === "jobs" && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(380px, 1fr))",
            gap: "24px",
          }}
        >
          {currentJobsList.length > 0 ? (
            currentJobsList.map((job, index) => {
              const statusColors = getStatusColor(job.status);
              const typeColors = getJobTypeColor(job.job_type);
              return (
                <div
                  key={job.unique_id || job.schedule_id || index}
                  className="job-card"
                  style={{
                    background: "white",
                    borderRadius: "20px",
                    padding: "28px",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                    border: `2px solid ${typeColors.border}`,
                    position: "relative",
                    overflow: "hidden",
                    animationDelay: `${index * 0.1}s`,
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      top: "16px",
                      right: "16px",
                      padding: "6px 12px",
                      background: typeColors.bg,
                      color: typeColors.text,
                      borderRadius: "8px",
                      fontSize: "11px",
                      fontWeight: "700",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      border: `1px solid ${typeColors.border}`,
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                      zIndex: 2,
                    }}
                  >
                    {job.job_type === "maintenance" ? (
                      <>
                        <BuildIcon style={{ fontSize: "14px" }} /> Maintenance
                      </>
                    ) : (
                      <>
                        <CleaningIcon style={{ fontSize: "14px" }} /> Commercial
                      </>
                    )}
                  </div>

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      marginBottom: "20px",
                      position: "relative",
                      zIndex: 1,
                    }}
                  >
                    <div style={{ flex: 1, paddingRight: "80px" }}>
                      <div
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "12px",
                          marginBottom: "12px",
                        }}
                      >
                        <div
                          style={{
                            width: "56px",
                            height: "56px",
                            borderRadius: "14px",
                            background: statusColors.bg,
                            border: `2px solid ${statusColors.border}`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "24px",
                            fontWeight: "700",
                            color: statusColors.text,
                            flexShrink: 0,
                          }}
                        >
                          {job.site_name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <h5
                            style={{
                              fontSize: "20px",
                              fontWeight: "700",
                              color: "#111827",
                              margin: 0,
                              marginBottom: "4px",
                              lineHeight: 1.2,
                            }}
                          >
                            {job.site_name}
                          </h5>
                          <p
                            style={{
                              fontSize: "13px",
                              color: "#6B7280",
                              margin: 0,
                              fontWeight: "500",
                            }}
                          >
                            {job.client_name}
                          </p>
                        </div>
                      </div>
                    </div>
                    <span
                      style={{
                        padding: "6px 14px",
                        background: statusColors.bg,
                        color: statusColors.text,
                        borderRadius: "10px",
                        fontSize: "12px",
                        fontWeight: "700",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        border: `2px solid ${statusColors.border}`,
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        position: "absolute",
                        top: "60px",
                        right: 0,
                      }}
                    >
                      {getStatusIcon(job.status)} {job.status.replace("_", " ")}
                    </span>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "12px",
                      marginBottom: "20px",
                    }}
                  >
                    {job.address && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                        }}
                      >
                        <div
                          style={{
                            width: "36px",
                            height: "36px",
                            borderRadius: "10px",
                            background: "#F0F9FF",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <LocationIcon
                            style={{ color: "#3B82F6", fontSize: "18px" }}
                          />
                        </div>
                        <div style={{ flex: 1 }}>
                          <div
                            style={{
                              fontSize: "11px",
                              color: "#6B7280",
                              fontWeight: "600",
                              marginBottom: "2px",
                            }}
                          >
                            Location
                          </div>
                          <div
                            style={{
                              fontSize: "14px",
                              color: "#111827",
                              fontWeight: "600",
                            }}
                          >
                            {job.location}
                          </div>
                        </div>
                      </div>
                    )}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                      }}
                    >
                      <div
                        style={{
                          width: "36px",
                          height: "36px",
                          borderRadius: "10px",
                          background: "#FEF3C7",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <CalendarIcon
                          style={{ color: "#F59E0B", fontSize: "18px" }}
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div
                          style={{
                            fontSize: "11px",
                            color: "#6B7280",
                            fontWeight: "600",
                            marginBottom: "2px",
                          }}
                        >
                          Scheduled
                        </div>
                        <div
                          style={{
                            fontSize: "14px",
                            color: "#111827",
                            fontWeight: "600",
                          }}
                        >
                          {format(new Date(job.scheduled_date), "MMM dd, yyyy")}
                        </div>
                      </div>
                    </div>
                  </div>

{/* Task/System and Service Number Grid */}
<div
  style={{
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "12px",
    marginBottom: "12px",
    paddingTop: "20px",
    borderTop: "2px solid #F3F4F6",
  }}
>
  <div style={{ background: "#F9FAFB", padding: "14px", borderRadius: "12px", border: "1px solid #E5E7EB", textAlign: "center" }}>
    <div style={{ fontSize: "10px", color: "#6B7280", fontWeight: "600", textTransform: "uppercase", marginBottom: "4px" }}>
      {job.job_type === "maintenance" ? "Task" : "System"}
    </div>
    <div style={{ fontSize: "16px", fontWeight: "800", color: "#111827" }}>
      {job.job_type === "maintenance" ? (job.task_name || "Maint.") : `${job.system_size} kW`}
    </div>
  </div>

  <div style={{ background: "#F9FAFB", padding: "14px", borderRadius: "12px", border: "1px solid #E5E7EB", textAlign: "center" }}>
    <div style={{ fontSize: "10px", color: "#6B7280", fontWeight: "600", textTransform: "uppercase", marginBottom: "4px" }}>Service</div>
    <div style={{ fontSize: "16px", fontWeight: "800", color: "#111827" }}>#{job.service_number}</div>
  </div>
</div>

{/* Efficiency Timer / Result Section */}
<div style={{ marginBottom: "16px" }}>
  <JobEfficiencyTimer job={job} />
</div>

{/* Approval badge — completed jobs only */}
{job.status === "completed" && (() => {
  const ab = getApprovalBadge(job.approval_status);
  return (
    <div style={{ display:"flex", alignItems:"center", gap:"8px", marginBottom:"16px", padding:"10px 14px", borderRadius:"12px", background: ab.bg, border:`1.5px solid ${ab.border}` }}>
      <span style={{ fontSize:"13px", fontWeight:"700", color: ab.color, display:"flex", alignItems:"center", gap:"6px" }}>
        {ab.icon} Client Approval: {ab.label}
      </span>
      {job.rejection_reason && (
        <span style={{ fontSize:"12px", color:"#DC2626", marginLeft:"auto", fontStyle:"italic" }}>
          "{job.rejection_reason}"
        </span>
      )}
    </div>
  );
})()}

                  <button
                    onClick={() => handleViewDetails(job)}
                    style={{
                      width: "100%",
                      padding: "14px",
                      background:
                        job.job_type === "maintenance"
                          ? "linear-gradient(135deg, #C026D3 0%, #86198F 100%)"
                          : "linear-gradient(135deg, #667EEA 0%, #764BA2 100%)",
                      color: "white",
                      border: "none",
                      borderRadius: "12px",
                      fontSize: "15px",
                      fontWeight: "700",
                      cursor: "pointer",
                      boxShadow:
                        job.job_type === "maintenance"
                          ? "0 4px 12px rgba(192, 38, 211, 0.4)"
                          : "0 4px 12px rgba(102, 126, 234, 0.4)",
                      transition: "all 0.2s",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "8px",
                    }}
                  >
                    <ViewIcon style={{ fontSize: "20px" }} /> View Details
                  </button>
                </div>
              );
            })
          ) : (
            <div
              style={{
                gridColumn: "1/-1",
                textAlign: "center",
                padding: "40px",
                color: "#6B7280",
              }}
            >
              No jobs found for this date.
            </div>
          )}
        </div>
      )}

      {/* Job Details Modal */}
      <Modal
        show={detailsOpen}
        onHide={() => {
          setIsEditing(false);
          setDetailsOpen(false);
        }}
        centered
        size="lg"
        style={{ zIndex: 9999 }}
      >
        <div style={{ borderRadius: "20px", overflow: "hidden", border: "none" }}>
          <Modal.Header
            closeButton
            style={{
              background:
                selectedJob?.job_type === "maintenance"
                  ? "linear-gradient(135deg, #C026D3 0%, #86198F 100%)"
                  : "linear-gradient(135deg, #667EEA 0%, #764BA2 100%)",
              border: "none",
              padding: "28px 32px",
            }}
          >
            {selectedJob && (
              <Modal.Title
                style={{
                  color: "white",
                  fontSize: "24px",
                  fontWeight: "700",
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  width: "100%",
                  justifyContent: "space-between",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  {selectedJob.job_type === "maintenance" ? (
                    <BuildIcon style={{ fontSize: "28px" }} />
                  ) : (
                    <DescriptionIcon style={{ fontSize: "28px" }} />
                  )}
                  {selectedJob.site_name}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  {!isEditing && (
                    <button
                      onClick={() => {
                        setEditData({ ...selectedJob });
                        setIsEditing(true);
                        
                      }}
                      style={{
                        padding: "6px 16px",
                        background: "rgba(255,255,255,0.2)",
                        color: "white",
                        border: "2px solid rgba(255,255,255,0.4)",
                        borderRadius: "10px",
                        fontSize: "13px",
                        fontWeight: "700",
                        cursor: "pointer",
                      }}
                    >
                      EDIT JOB
                      
                      
                    </button>
                  )}
                  <span
                    style={{
                      padding: "6px 14px",
                      background: "rgba(255,255,255,0.2)",
                      borderRadius: "10px",
                      fontSize: "12px",
                      fontWeight: "700",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      border: "2px solid rgba(255,255,255,0.3)",
                    }}
                  >
                    {getStatusIcon(selectedJob.status)}{" "}
                    {selectedJob.status.replace("_", " ")}
                  </span>
                </div>
              </Modal.Title>
            )}
          </Modal.Header>

          {detailsLoading ? (
            <div style={{ padding: "80px", textAlign: "center" }}>
              <div
                style={{
                  width: "48px",
                  height: "48px",
                  border: "4px solid #E9ECEF",
                  borderTop: "4px solid #667EEA",
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite",
                  margin: "0 auto 16px",
                }}
              />
              <p style={{ color: "#6C757D", fontWeight: "500" }}>
                Loading details...
              </p>
            </div>
          ) : (
            selectedJob && (
              <>
                <Modal.Body
                  style={{
                    padding: "32px",
                    maxHeight: "70vh",
                    overflowY: "auto",
                  }}
                >
                  {/* SITE INFORMATION */}
                  <div
                    style={{
                      background: "#F9FAFB",
                      padding: "24px",
                      borderRadius: "16px",
                      marginBottom: "24px",
                      border: "2px solid #E5E7EB",
                    }}
                  >
                    <h6
                      style={{
                        fontSize: "16px",
                        fontWeight: "700",
                        color: "#111827",
                        marginBottom: "16px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <LocationIcon
                        style={{ color: "#667EEA", fontSize: "22px" }}
                      />{" "}
                      Site Information
                    </h6>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "16px",
                      }}
                    >
                      <div>
                        <div
                          style={{
                            fontSize: "11px",
                            color: "#6B7280",
                            fontWeight: "600",
                            marginBottom: "4px",
                            textTransform: "uppercase",
                          }}
                        >
                          Client
                        </div>
                        <div
                          style={{
                            fontSize: "14px",
                            color: "#111827",
                            fontWeight: "600",
                          }}
                        >
                          {selectedJob.client_name}
                        </div>
                      </div>
                      <div>
                        <div
                          style={{
                            fontSize: "11px",
                            color: "#6B7280",
                            fontWeight: "600",
                            marginBottom: "4px",
                            textTransform: "uppercase",
                          }}
                        >
                          Job Type
                        </div>
                        <div
                          style={{
                            fontSize: "14px",
                            color: "#111827",
                            fontWeight: "600",
                          }}
                        >
                          {selectedJob.job_type === "maintenance"
                            ? "Maintenance Service"
                            : "Commercial Cleaning"}
                        </div>
                      </div>
                      {selectedJob.system_size > 0 && (
                        <div>
                          <div
                            style={{
                              fontSize: "11px",
                              color: "#6B7280",
                              fontWeight: "600",
                              marginBottom: "4px",
                              textTransform: "uppercase",
                            }}
                          >
                            System Size
                          </div>
                          <div
                            style={{
                              fontSize: "14px",
                              color: "#111827",
                              fontWeight: "600",
                            }}
                          >
                            {selectedJob.system_size} kW
                          </div>
                        </div>
                      )}
                      <div>
                        <div
                          style={{
                            fontSize: "11px",
                            color: "#6B7280",
                            fontWeight: "600",
                            marginBottom: "4px",
                            textTransform: "uppercase",
                          }}
                        >
                          Service Number
                        </div>
                        <div
                          style={{
                            fontSize: "14px",
                            color: "#111827",
                            fontWeight: "600",
                          }}
                        >
                          #{selectedJob.service_number}
                        </div>
                      </div>
                      {selectedJob.contact_person && (
                        <div>
                          <div
                            style={{
                              fontSize: "11px",
                              color: "#6B7280",
                              fontWeight: "600",
                              marginBottom: "4px",
                              textTransform: "uppercase",
                            }}
                          >
                            Contact Person
                          </div>
                          <div
                            style={{
                              fontSize: "14px",
                              color: "#111827",
                              fontWeight: "600",
                            }}
                          >
                            {selectedJob.contact_person}
                          </div>
                        </div>
                      )}
                      {selectedJob.contact_number && (
                        <div>
                          <div
                            style={{
                              fontSize: "11px",
                              color: "#6B7280",
                              fontWeight: "600",
                              marginBottom: "4px",
                              textTransform: "uppercase",
                            }}
                          >
                            Contact Number
                          </div>
                          <div
                            style={{
                              fontSize: "14px",
                              color: "#111827",
                              fontWeight: "600",
                            }}
                          >
                            {selectedJob.contact_number}
                          </div>
                        </div>
                      )}
                      {selectedJob.team_lead_name && (
                        <div>
                          <div
                            style={{
                              fontSize: "11px",
                              color: "#6B7280",
                              fontWeight: "600",
                              marginBottom: "4px",
                              textTransform: "uppercase",
                            }}
                          >
                            Team Lead
                          </div>
                          <div
                            style={{
                              fontSize: "14px",
                              color: "#111827",
                              fontWeight: "600",
                            }}
                          >
                            {selectedJob.team_lead_name}
                          </div>
                        </div>
                      )}
                      {selectedJob.address && (
                        <div style={{ gridColumn: "1 / -1" }}>
                          <div
                            style={{
                              fontSize: "11px",
                              color: "#6B7280",
                              fontWeight: "600",
                              marginBottom: "4px",
                              textTransform: "uppercase",
                            }}
                          >
                            Address
                          </div>
                          <div
                            style={{
                              fontSize: "14px",
                              color: "#111827",
                              fontWeight: "600",
                            }}
                          >
                            {selectedJob.address}, {selectedJob.city}{" "}
                            {selectedJob.state ? ", " + selectedJob.state : ""}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Job Timeline */}
                  <div
                    style={{
                      background: "#F9FAFB",
                      padding: "24px",
                      borderRadius: "16px",
                      marginBottom: "24px",
                      border: "2px solid #E5E7EB",
                    }}
                  >
                    <h6
                      style={{
                        fontSize: "16px",
                        fontWeight: "700",
                        color: "#111827",
                        marginBottom: "16px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <TimeIcon
                        style={{ color: "#667EEA", fontSize: "22px" }}
                      />{" "}
                      Job Timeline
                    </h6>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "16px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "start",
                          gap: "12px",
                        }}
                      >
                        <div
                          style={{
                            width: "40px",
                            height: "40px",
                            borderRadius: "12px",
                            background: "#EFF6FF",
                            border: "2px solid #3B82F6",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <CalendarIcon
                            style={{ color: "#3B82F6", fontSize: "20px" }}
                          />
                        </div>
                        <div style={{ flex: 1 }}>
                          <div
                            style={{
                              fontSize: "13px",
                              fontWeight: "700",
                              color: "#3B82F6",
                              marginBottom: "2px",
                            }}
                          >
                            Scheduled
                          </div>
                          {isEditing ? (
                            <input
                              type="date"
                              className="form-control"
                              value={formatDateForInput(editData?.scheduled_date)}
                              onChange={(e) => setEditData({ ...editData, scheduled_date: e.target.value })}
                              style={{ padding: "4px 8px", fontSize: "12px", borderRadius: "8px", border: "1px solid #D1D5DB" }}
                            />
                          ) : (
                            <div style={{ fontSize: "12px", color: "#6B7280" }}>
                              {format(
                                new Date(selectedJob.scheduled_date),
                                "MMM dd, yyyy",
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      {[
                        { label: "Site Arrival", key: "reached_location_at", icon: <LocationIcon style={{ color: "#F59E0B" }} />, bg: "#FEF3C7", border: "#F59E0B" },
                        { label: "Work Area Reached", key: "started_at", icon: <ScheduleIcon style={{ color: "#10B981" }} />, bg: "#ECFDF5", border: "#10B981" },
                        { label: "Work Started", key: "before_photos_at", fallbackKey: "before_photo_at", icon: <PhotoIcon style={{ color: "#A855F7" }} />, bg: "#F3E8FF", border: "#A855F7" },
                        { label: "Work Completed", key: "after_photos_at", fallbackKey: "after_photo_at", icon: <PhotoIcon style={{ color: "#EC4899" }} />, bg: "#FCE7F3", border: "#EC4899" },
                        { label: "Site Exit", key: "completed_at", icon: <CheckCircleIcon style={{ color: "#10B981" }} />, bg: "#ECFDF5", border: "#10B981" }
                      ].map((item) => {
                        const timestamp = selectedJob[item.key] || selectedJob[item.fallbackKey];
                        const editTimestamp = editData?.[item.key] || editData?.[item.fallbackKey];
                        
                        if (!timestamp && !isEditing) return null;
                        
                        return (
                          <div key={item.key} style={{ display: "flex", alignItems: "start", gap: "12px" }}>
                            <div style={{ width: "40px", height: "40px", borderRadius: "12px", background: item.bg, border: `2px solid ${item.border}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                              {item.icon}
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: "13px", fontWeight: "700", color: item.border, marginBottom: "2px" }}>{item.label}</div>
                              {isEditing ? (
                                <input
                                  type="datetime-local"
                                  className="form-control"
                                  value={formatDateTimeForInput(editTimestamp)}
                                  onChange={(e) => setEditData({ ...editData, [item.key]: e.target.value })}
                                  style={{ padding: "4px 8px", fontSize: "12px", borderRadius: "8px", border: "1px solid #D1D5DB" }}
                                />
                              ) : (
                                <div style={{ fontSize: "12px", color: "#6B7280" }}>
                                  {timestamp ? format(new Date(timestamp), "MMM dd, yyyy • hh:mm a") : "Pending"}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Photos */}
                  {(selectedJob.before_photos || selectedJob.after_photos || selectedJob.temp_voltage_photos || selectedJob.before_photo_url || selectedJob.after_photo_url || isEditing) && (
                    <div
                      style={{
                        background: "#F9FAFB",
                        padding: "24px",
                        borderRadius: "16px",
                        marginBottom: "24px",
                        border: "2px solid #E5E7EB",
                      }}
                    >
                      <h6 style={{ fontSize: "16px", fontWeight: "700", color: "#111827", marginBottom: "16px", display: "flex", alignItems: "center", gap: "8px" }}>
                        <PhotoIcon style={{ color: "#667EEA", fontSize: "22px" }} /> Photo Gallery
                      </h6>
                      {[
                        { key: 'before_photos',       fallbackKey: 'before_photo_url', label: '📷 Before Photos' },
                        { key: 'after_photos',        fallbackKey: 'after_photo_url',  label: '📷 After Photos' },
                        { key: 'temp_voltage_photos', fallbackKey: null,               label: '⚡ Tool Box Talk' },
                      ].map(({ key, fallbackKey, label }) => {
                        const targetData = isEditing ? editData : selectedJob;
                        let raw = targetData?.[key];
                        
                        let photos = [];
                        const parseRaw = (val) => {
                          if (!val) return [];
                          let parsed = val;
                          try { if (typeof parsed === 'string') parsed = JSON.parse(parsed); } catch(e) {}
                          try { if (typeof parsed === 'string') parsed = JSON.parse(parsed); } catch(e) {} // Double parse safety
                          if (Array.isArray(parsed)) return parsed;
                          if (typeof parsed === 'string') return parsed.split(',').map(s => s.trim()).filter(Boolean);
                          return [parsed];
                        };

                        photos = parseRaw(raw);
                        if (photos.length === 0 && fallbackKey && targetData?.[fallbackKey]) {
                          photos = parseRaw(targetData[fallbackKey]);
                        }
                        photos = photos.filter(Boolean);

                        if (photos.length === 0 && !isEditing) return null;

                        return (
                          <div key={key} style={{ marginBottom: "20px" }}>
                            <div style={{ fontSize: "13px", fontWeight: "700", color: "#6B7280", marginBottom: "12px" }}>
                              {label}
                            </div>
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: "12px" }}>
                              {photos.map((photo, index) => (
                                <AutoFallbackImage 
                                  key={`${key}-${index}`}
                                  photoRaw={photo}
                                  alt={`${label} ${index + 1}`}
                                  isEditing={isEditing}
                                  onDelete={() => {
                                    const updated = photos.filter((_, i) => i !== index);
                                    handlePhotoDelete(key, updated);
                                  }}
                                />
                              ))}
                              {isEditing && (
                                <label style={{ height: '150px', border: '2px dashed #667EEA', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#667EEA', fontSize: '12px', fontWeight: '700', flexDirection: 'column', gap: '4px' }}>
                                  + Add Photo
                                  <input 
                                    type="file" 
                                    accept="image/*"
                                    hidden 
                                    onChange={(e) => {
                                      if (e.target.files && e.target.files[0]) {
                                        handleImageUpload(e.target.files[0], key);
                                      }
                                    }} 
                                  />
                                </label>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {/* CONDITIONAL REPORT SECTION */}
                  {selectedJob.status === "completed" &&
                    (selectedJob.job_type === "maintenance" ? (
                      // MAINTENANCE REPORT
                      <div
                        style={{
                          background: "#FDF4FF",
                          padding: "24px",
                          borderRadius: "16px",
                          marginBottom: "24px",
                          border: "2px solid #C026D3",
                        }}
                      >
                        <h6
                          style={{
                            fontSize: "16px",
                            fontWeight: "700",
                            color: "#86198F",
                            marginBottom: "16px",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <EngineeringIcon
                            style={{ color: "#C026D3", fontSize: "22px" }}
                          />{" "}
                          Maintenance Report
                        </h6>

                        <div style={{ marginBottom: "16px" }}>
                          <div
                            style={{
                              fontSize: "11px",
                              color: "#6B7280",
                              fontWeight: "600",
                              marginBottom: "4px",
                              textTransform: "uppercase",
                            }}
                          >
                            Task Name
                          </div>
                          {isEditing ? (
                            <input
                              type="text"
                              className="form-control"
                              value={editData?.task_name || ""}
                              onChange={(e) => setEditData({ ...editData, task_name: e.target.value })}
                              style={{ fontSize: "15px", fontWeight: "700", padding: "8px", borderRadius: "8px", border: "1px solid #D1D5DB" }}
                            />
                          ) : (
                            <div
                              style={{
                                fontSize: "15px",
                                color: "#111827",
                                fontWeight: "700",
                              }}
                            >
                              {selectedJob.task_name || "General Maintenance"}
                            </div>
                          )}
                        </div>

                        {(selectedJob.task_description || selectedJob.maintenance_notes || isEditing) && (
                          <div
                            style={{
                              background: "white",
                              padding: "16px",
                              borderRadius: "12px",
                              border: "1px solid #E5E7EB",
                              marginBottom: "16px",
                            }}
                          >
                            <div
                              style={{
                                fontSize: "11px",
                                color: "#6B7280",
                                fontWeight: "600",
                                marginBottom: "8px",
                                textTransform: "uppercase",
                              }}
                            >
                              Description & Notes
                            </div>
                            {isEditing ? (
                              <textarea
                                className="form-control"
                                value={editData?.task_description || editData?.maintenance_notes || ""}
                                onChange={(e) => setEditData({ ...editData, task_description: e.target.value })}
                                rows={4}
                                style={{ fontSize: "14px", padding: "8px", borderRadius: "8px", border: "1px solid #D1D5DB" }}
                              />
                            ) : (
                              <div
                                style={{
                                  fontSize: "14px",
                                  color: "#374151",
                                  lineHeight: "1.6",
                                }}
                              >
                                {selectedJob.task_description ||
                                  selectedJob.maintenance_notes}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Static Checks for Maintenance Visualization */}
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: "12px",
                          }}
                        >
                          <div
                            style={{
                              background: "white",
                              padding: "12px",
                              borderRadius: "10px",
                              border: "1px solid #E5E7EB",
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <span
                              style={{
                                fontSize: "13px",
                                fontWeight: "600",
                                color: "#4B5563",
                              }}
                            >
                              Safety Check
                            </span>
                            <span
                              style={{
                                fontSize: "12px",
                                fontWeight: "700",
                                color: "#059669",
                                background: "#D1FAE5",
                                padding: "2px 8px",
                                borderRadius: "6px",
                              }}
                            >
                              PASSED
                            </span>
                          </div>
                          <div
                            style={{
                              background: "white",
                              padding: "12px",
                              borderRadius: "10px",
                              border: "1px solid #E5E7EB",
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <span
                              style={{
                                fontSize: "13px",
                                fontWeight: "600",
                                color: "#4B5563",
                              }}
                            >
                              System Status
                            </span>
                            <span
                              style={{
                                fontSize: "12px",
                                fontWeight: "700",
                                color: "#059669",
                                background: "#D1FAE5",
                                padding: "2px 8px",
                                borderRadius: "6px",
                              }}
                            >
                              VERIFIED
                            </span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      // COMMERCIAL FSR
                      <div
                        style={{
                          background: "#F9FAFB",
                          padding: "24px",
                          borderRadius: "16px",
                          marginBottom: "24px",
                          border: "2px solid #E5E7EB",
                        }}
                      >
                        <h6
                          style={{
                            fontSize: "16px",
                            fontWeight: "700",
                            color: "#111827",
                            marginBottom: "16px",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <DescriptionIcon
                            style={{ color: "#667EEA", fontSize: "22px" }}
                          />{" "}
                          Field Service Report
                        </h6>
                        
                        {/* Main Fields Grid */}
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: "12px",
                            marginBottom: "16px",
                          }}
                        >
                          {/* Cable Condition */}
                          <div
                            style={{
                              background: "white",
                              padding: "16px",
                              borderRadius: "12px",
                              border: "1px solid #E5E7EB",
                            }}
                          >
                            <div
                              style={{
                                fontSize: "11px",
                                color: "#6B7280",
                                fontWeight: "600",
                                marginBottom: "6px",
                                textTransform: "uppercase",
                              }}
                            >
                              Cable Condition
                            </div>
                            {isEditing ? (
                              <select
                                className="form-control"
                                value={editData?.cable_condition || ""}
                                onChange={(e) => setEditData({ ...editData, cable_condition: e.target.value })}
                                style={{ 
                                  fontSize: "14px", 
                                  fontWeight: "700", 
                                  padding: "8px", 
                                  borderRadius: "8px", 
                                  border: "1px solid #D1D5DB" 
                                }}
                              >
                                <option value="">Select...</option>
                                <option value="Good">Good</option>
                                <option value="Bad">Bad</option>
                              </select>
                            ) : (
                              <div
                                style={{
                                  fontSize: "14px",
                                  color: "#111827",
                                  fontWeight: "700",
                                }}
                              >
                                {selectedJob.cable_condition || "N/A"}
                              </div>
                            )}
                          </div>

                          {/* Cable Quantity */}
                          <div
                            style={{
                              background: "white",
                              padding: "16px",
                              borderRadius: "12px",
                              border: "1px solid #E5E7EB",
                            }}
                          >
                            <div
                              style={{
                                fontSize: "11px",
                                color: "#6B7280",
                                fontWeight: "600",
                                marginBottom: "6px",
                                textTransform: "uppercase",
                              }}
                            >
                              Cable Quantity
                            </div>
                            {isEditing ? (
                              <input
                                type="text"
                                className="form-control"
                                value={editData?.cable_quantity || ""}
                                onChange={(e) => setEditData({ ...editData, cable_quantity: e.target.value })}
                                placeholder="e.g. 10 meters"
                                style={{ 
                                  fontSize: "14px", 
                                  fontWeight: "700", 
                                  padding: "8px", 
                                  borderRadius: "8px", 
                                  border: "1px solid #D1D5DB" 
                                }}
                              />
                            ) : (
                              <div
                                style={{
                                  fontSize: "14px",
                                  color: "#111827",
                                  fontWeight: "700",
                                }}
                              >
                                {selectedJob.cable_quantity || "N/A"}
                              </div>
                            )}
                          </div>

                          {/* Panel Damage */}
                          <div
                            style={{
                              background: "white",
                              padding: "16px",
                              borderRadius: "12px",
                              border: "1px solid #E5E7EB",
                            }}
                          >
                            <div
                              style={{
                                fontSize: "11px",
                                color: "#6B7280",
                                fontWeight: "600",
                                marginBottom: "6px",
                                textTransform: "uppercase",
                              }}
                            >
                              Panel Damage
                            </div>
                            {isEditing ? (
                              <select
                                className="form-control"
                                value={editData?.panel_damage || ""}
                                onChange={(e) => setEditData({ ...editData, panel_damage: e.target.value })}
                                style={{ 
                                  fontSize: "14px", 
                                  fontWeight: "700", 
                                  padding: "8px", 
                                  borderRadius: "8px", 
                                  border: "1px solid #D1D5DB" 
                                }}
                              >
                                <option value="">Select...</option>
                                <option value="Yes">Yes</option>
                                <option value="No">No</option>
                              </select>
                            ) : (
                              <div
                                style={{
                                  fontSize: "14px",
                                  color: "#111827",
                                  fontWeight: "700",
                                }}
                              >
                                {selectedJob.panel_damage || "N/A"}
                              </div>
                            )}
                          </div>

                          {/* Panel Brand */}
                          <div
                            style={{
                              background: "white",
                              padding: "16px",
                              borderRadius: "12px",
                              border: "1px solid #E5E7EB",
                            }}
                          >
                            <div
                              style={{
                                fontSize: "11px",
                                color: "#6B7280",
                                fontWeight: "600",
                                marginBottom: "6px",
                                textTransform: "uppercase",
                              }}
                            >
                              Panel Brand
                            </div>
                            {isEditing ? (
                              <input
                                type="text"
                                className="form-control"
                                value={editData?.panel_brand || ""}
                                onChange={(e) => setEditData({ ...editData, panel_brand: e.target.value })}
                                placeholder="e.g. Canadian Solar"
                                style={{ 
                                  fontSize: "14px", 
                                  fontWeight: "700", 
                                  padding: "8px", 
                                  borderRadius: "8px", 
                                  border: "1px solid #D1D5DB" 
                                }}
                              />
                            ) : (
                              <div
                                style={{
                                  fontSize: "14px",
                                  color: "#111827",
                                  fontWeight: "700",
                                }}
                              >
                                {selectedJob.panel_brand || "N/A"}
                              </div>
                            )}
                          </div>

                          {/* Inverter Alarm */}
                          <div
                            style={{
                              background: "white",
                              padding: "16px",
                              borderRadius: "12px",
                              border: "1px solid #E5E7EB",
                            }}
                          >
                            <div
                              style={{
                                fontSize: "11px",
                                color: "#6B7280",
                                fontWeight: "600",
                                marginBottom: "6px",
                                textTransform: "uppercase",
                              }}
                            >
                              Inverter Alarm
                            </div>
                            {isEditing ? (
                              <select
                                className="form-control"
                                value={editData?.inverter_alarm || ""}
                                onChange={(e) => setEditData({ ...editData, inverter_alarm: e.target.value })}
                                style={{ 
                                  fontSize: "14px", 
                                  fontWeight: "700", 
                                  padding: "8px", 
                                  borderRadius: "8px", 
                                  border: "1px solid #D1D5DB" 
                                }}
                              >
                                <option value="">Select...</option>
                                <option value="Yes">Yes</option>
                                <option value="No">No</option>
                              </select>
                            ) : (
                              <div
                                style={{
                                  fontSize: "14px",
                                  color: "#111827",
                                  fontWeight: "700",
                                }}
                              >
                                {selectedJob.inverter_alarm || "N/A"}
                              </div>
                            )}
                          </div>

                          {/* Alarm Code */}
                          <div
                            style={{
                              background: "white",
                              padding: "16px",
                              borderRadius: "12px",
                              border: "1px solid #E5E7EB",
                            }}
                          >
                            <div
                              style={{
                                fontSize: "11px",
                                color: "#6B7280",
                                fontWeight: "600",
                                marginBottom: "6px",
                                textTransform: "uppercase",
                              }}
                            >
                              Alarm Code
                            </div>
                            {isEditing ? (
                              <input
                                type="text"
                                className="form-control"
                                value={editData?.alarm_code || ""}
                                onChange={(e) => setEditData({ ...editData, alarm_code: e.target.value })}
                                placeholder="e.g. E001"
                                style={{ 
                                  fontSize: "14px", 
                                  fontWeight: "700", 
                                  padding: "8px", 
                                  borderRadius: "8px", 
                                  border: "1px solid #D1D5DB" 
                                }}
                              />
                            ) : (
                              <div
                                style={{
                                  fontSize: "14px",
                                  color: "#111827",
                                  fontWeight: "700",
                                }}
                              >
                                {selectedJob.alarm_code || "N/A"}
                              </div>
                            )}
                          </div>

                          {/* Potential Shading */}
                          <div
                            style={{
                              background: "white",
                              padding: "16px",
                              borderRadius: "12px",
                              border: "1px solid #E5E7EB",
                            }}
                          >
                            <div
                              style={{
                                fontSize: "11px",
                                color: "#6B7280",
                                fontWeight: "600",
                                marginBottom: "6px",
                                textTransform: "uppercase",
                              }}
                            >
                              Potential Shading
                            </div>
                            {isEditing ? (
                              <select
                                className="form-control"
                                value={editData?.potential_shading || ""}
                                onChange={(e) => setEditData({ ...editData, potential_shading: e.target.value })}
                                style={{ 
                                  fontSize: "14px", 
                                  fontWeight: "700", 
                                  padding: "8px", 
                                  borderRadius: "8px", 
                                  border: "1px solid #D1D5DB" 
                                }}
                              >
                                <option value="">Select...</option>
                                <option value="Yes">Yes</option>
                                <option value="No">No</option>
                              </select>
                            ) : (
                              <div
                                style={{
                                  fontSize: "14px",
                                  color: "#111827",
                                  fontWeight: "700",
                                }}
                              >
                                {selectedJob.potential_shading || "N/A"}
                              </div>
                            )}
                          </div>

                          {/* Total Panels Cleaned */}
                          <div
                            style={{
                              background: "white",
                              padding: "16px",
                              borderRadius: "12px",
                              border: "1px solid #E5E7EB",
                            }}
                          >
                            <div
                              style={{
                                fontSize: "11px",
                                color: "#6B7280",
                                fontWeight: "600",
                                marginBottom: "6px",
                                textTransform: "uppercase",
                              }}
                            >
                              Total Panels Cleaned
                            </div>
                            {isEditing ? (
                              <input
                                type="number"
                                className="form-control"
                                value={editData?.total_panels_cleaned || ""}
                                onChange={(e) => setEditData({ ...editData, total_panels_cleaned: e.target.value })}
                                placeholder="e.g. 150"
                                style={{ 
                                  fontSize: "14px", 
                                  fontWeight: "700", 
                                  padding: "8px", 
                                  borderRadius: "8px", 
                                  border: "1px solid #D1D5DB" 
                                }}
                              />
                            ) : (
                              <div
                                style={{
                                  fontSize: "14px",
                                  color: "#111827",
                                  fontWeight: "700",
                                }}
                              >
                                {selectedJob.total_panels_cleaned || "N/A"}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Shading Details */}
                        {(selectedJob.potential_shading === "Yes" || (isEditing && editData?.potential_shading === "Yes")) && (
                          <div
                            style={{
                              background: "white",
                              padding: "16px",
                              borderRadius: "12px",
                              border: "1px solid #E5E7EB",
                              marginBottom: "12px"
                            }}
                          >
                            <div
                              style={{
                                fontSize: "11px",
                                color: "#6B7280",
                                fontWeight: "600",
                                marginBottom: "6px",
                                textTransform: "uppercase",
                              }}
                            >
                              Shading Details
                            </div>
                            {isEditing ? (
                              <textarea
                                className="form-control"
                                value={editData?.shading_details || ""}
                                onChange={(e) => setEditData({ ...editData, shading_details: e.target.value })}
                                rows={3}
                                placeholder="Describe shading issues..."
                                style={{ 
                                  fontSize: "14px", 
                                  padding: "8px", 
                                  borderRadius: "8px", 
                                  border: "1px solid #D1D5DB" 
                                }}
                              />
                            ) : (
                              <div
                                style={{
                                  fontSize: "14px",
                                  color: "#374151",
                                  lineHeight: "1.6",
                                }}
                              >
                                {selectedJob.shading_details || "No details provided"}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Boolean Checkboxes */}
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                            gap: "12px",
                            marginTop: "16px",
                          }}
                        >
                          {[
                            { label: "Rusting", key: "rusting", icon: "🔧" },
                            { label: "Bird Dropping", key: "bird_dropping", icon: "🦅" },
                            { label: "Moss & Debris", key: "mos_and_debris", icon: "🍃" },
                            { label: "Earthing", key: "earthing", icon: "⚡" },
                          ].map((field) => (
                            <div
                              key={field.key}
                              style={{
                                background: isEditing && editData?.[field.key] 
                                  ? "#ECFDF5" 
                                  : selectedJob[field.key] 
                                  ? "#ECFDF5" 
                                  : "white",
                                padding: "12px 16px",
                                borderRadius: "12px",
                                border: isEditing && editData?.[field.key]
                                  ? "2px solid #10B981"
                                  : selectedJob[field.key]
                                  ? "2px solid #10B981"
                                  : "1px solid #E5E7EB",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                cursor: isEditing ? "pointer" : "default",
                              }}
                              onClick={() => {
                                if (isEditing) {
                                  setEditData({ 
                                    ...editData, 
                                    [field.key]: !editData?.[field.key] 
                                  });
                                }
                              }}
                            >
                              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                <span style={{ fontSize: "18px" }}>{field.icon}</span>
                                <span
                                  style={{
                                    fontSize: "13px",
                                    fontWeight: "600",
                                    color: "#4B5563",
                                  }}
                                >
                                  {field.label}
                                </span>
                              </div>
                              {isEditing ? (
                                <input
                                  type="checkbox"
                                  checked={editData?.[field.key] || false}
                                  onChange={(e) => setEditData({ ...editData, [field.key]: e.target.checked })}
                                  style={{ 
                                    width: "18px", 
                                    height: "18px", 
                                    cursor: "pointer" 
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              ) : (
                                <span
                                  style={{
                                    fontSize: "18px",
                                    fontWeight: "700",
                                    color: selectedJob[field.key] ? "#10B981" : "#9CA3AF",
                                  }}
                                >
                                  {selectedJob[field.key] ? "✓" : "✗"}
                                </span>
                              )}
                            </div>
                          ))}
                        </div>

                        {/* Additional Notes */}
                        <div
                          style={{
                            background: "white",
                            padding: "16px",
                            borderRadius: "12px",
                            border: "1px solid #E5E7EB",
                            marginTop: "16px"
                          }}
                        >
                          <div
                            style={{
                              fontSize: "11px",
                              color: "#6B7280",
                              fontWeight: "600",
                              marginBottom: "6px",
                              textTransform: "uppercase",
                            }}
                          >
                            Additional Notes
                          </div>
                          {isEditing ? (
                            <textarea
                              className="form-control"
                              value={editData?.additional_notes || ""}
                              onChange={(e) => setEditData({ ...editData, additional_notes: e.target.value })}
                              rows={3}
                              placeholder="Any additional observations..."
                              style={{ 
                                fontSize: "14px", 
                                padding: "8px", 
                                borderRadius: "8px", 
                                border: "1px solid #D1D5DB" 
                              }}
                            />
                          ) : (
                            <div
                              style={{
                                fontSize: "14px",
                                color: "#374151",
                                lineHeight: "1.6",
                              }}
                            >
                              {selectedJob.additional_notes || "No additional notes"}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}

                  {/* Customer Notes */}
                  {(selectedJob.notes || isEditing) && (
                    <div
                      style={{
                        background: "#FFFBEB",
                        padding: "20px",
                        borderRadius: "16px",
                        border: "2px solid #FCD34D",
                        marginBottom: "16px",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "13px",
                          fontWeight: "700",
                          color: "#92400E",
                          marginBottom: "8px",
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                        }}
                      >
                        <NotesIcon /> Notes
                      </div>
                      {isEditing ? (
                        <textarea
                          className="form-control"
                          value={editData?.notes || ""}
                          onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                          rows={3}
                          style={{ fontSize: "14px", padding: "8px", borderRadius: "8px", border: "1px solid #D1D5DB" }}
                        />
                      ) : (
                        <div
                          style={{
                            fontSize: "14px",
                            color: "#78350F",
                            lineHeight: "1.6",
                          }}
                        >
                          {selectedJob.notes}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Customer Signature */}
                  {selectedJob.customer_signature && (
                    <div
                      style={{
                        background: "white",
                        padding: "16px",
                        borderRadius: "12px",
                        border: "1px solid #E5E7EB",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "11px",
                          color: "#6B7280",
                          fontWeight: "600",
                          marginBottom: "8px",
                        }}
                      >
                        Customer Signature
                      </div>
                      <img
                        src={
                          selectedJob.customer_signature.startsWith("http")
                            ? selectedJob.customer_signature
                            : `https://app.sowashusa.com/uploads/signatures/${selectedJob.customer_signature}`
                        }
                        alt="Customer Signature"
                        style={{
                          width: "100%",
                          maxWidth: "300px",
                          height: "100px",
                          objectFit: "contain",
                          border: "1px solid #E5E7EB",
                          borderRadius: "8px",
                          background: "white",
                        }}
                      />
                    </div>
                  )}

                  {/* FSR Photo */}
                 {selectedJob.fsr_photo && (
                    <div
                      style={{
                        background: "white",
                        padding: "16px",
                        borderRadius: "12px",
                        border: "1px solid #E5E7EB",
                        marginTop: "16px",
                      }}
                    >
                      <div style={{ fontSize: "11px", color: "#6B7280", fontWeight: "600", marginBottom: "8px" }}>
                        FSR Photo
                      </div>
                      <AutoFallbackImage 
                         photoRaw={selectedJob.fsr_photo}
                         alt="FSR"
                         isEditing={false}
                         customStyle={{
                           maxWidth: "260px",
                           height: "180px",
                           objectFit: "cover",
                           border: "1px solid #E5E7EB",
                           borderRadius: "8px",
                           cursor: "pointer",
                         }}
                      />
                    </div>
                  )}
                </Modal.Body>

                <Modal.Footer
                  style={{
                    borderTop: "2px solid #F3F4F6",
                    padding: "20px 32px",
                    background: "#FAFBFC",
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px",
                  }}
                >
                  {/* ── Approval Panel (completed jobs only, view mode only) ── */}
                  {!isEditing && selectedJob.status === "completed" && (() => {
                    const ab = getApprovalBadge(selectedJob.approval_status);
                    return (
                      <div style={{ width:"100%", padding:"14px 16px", borderRadius:"12px", background: ab.bg, border:`1.5px solid ${ab.border}` }}>
                        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:"10px" }}>
                          {/* Status label */}
                          <div style={{ display:"flex", alignItems:"center", gap:"8px" }}>
                            <span style={{ fontSize:"13px", fontWeight:"800", color: ab.color }}>
                              {ab.icon} Client Visibility: {ab.label}
                            </span>
                            {selectedJob.rejection_reason && (
                              <span style={{ fontSize:"12px", color:"#9CA3AF", fontStyle:"italic" }}>
                                — "{selectedJob.rejection_reason}"
                              </span>
                            )}
                          </div>
                          {/* Action buttons */}
                          <div style={{ display:"flex", gap:"8px", flexWrap:"wrap" }}>
                            {selectedJob.approval_status !== "approved" && (
                              <button
                                onClick={handleApprove}
                                disabled={approving}
                                style={{ padding:"8px 18px", background: approving ? "#6EE7B7" : "#10B981", color:"white", border:"none", borderRadius:"8px", fontSize:"13px", fontWeight:"700", cursor: approving ? "not-allowed":"pointer", display:"flex", alignItems:"center", gap:"6px" }}
                              >
                                {approving
                                  ? <><span style={{ width:"12px", height:"12px", border:"2px solid rgba(255,255,255,0.4)", borderTop:"2px solid white", borderRadius:"50%", display:"inline-block", animation:"spin 0.8s linear infinite" }} /> Approving...</>
                                  : <><ApproveIcon style={{ fontSize:"16px" }} /> Approve</>
                                }
                              </button>
                            )}
                            {selectedJob.approval_status !== "rejected" && !showRejectInput && (
                              <button
                                onClick={() => setShowRejectInput(true)}
                                style={{ padding:"8px 18px", background:"#FEF2F2", color:"#DC2626", border:"1.5px solid #FECACA", borderRadius:"8px", fontSize:"13px", fontWeight:"700", cursor:"pointer", display:"flex", alignItems:"center", gap:"6px" }}
                              >
                                <RejectIcon style={{ fontSize:"16px" }} /> Reject
                              </button>
                            )}
                            {selectedJob.approval_status !== "pending" && (
                              <button
                                onClick={handleUndoApproval}
                                style={{ padding:"8px 14px", background:"white", color:"#6B7280", border:"1px solid #E5E7EB", borderRadius:"8px", fontSize:"12px", fontWeight:"600", cursor:"pointer" }}
                              >
                                ↩ Undo
                              </button>
                            )}
                          </div>
                        </div>
                        {/* Reject reason inline input */}
                        {showRejectInput && (
                          <div style={{ marginTop:"12px", display:"flex", gap:"8px", alignItems:"stretch" }}>
                            <input
                              type="text"
                              placeholder="Reason for rejection (optional)"
                              value={rejectReason}
                              onChange={e => setRejectReason(e.target.value)}
                              onKeyDown={e => { if (e.key === "Enter") handleReject(); if (e.key === "Escape") { setShowRejectInput(false); setRejectReason(""); } }}
                              autoFocus
                              style={{ flex:1, padding:"9px 14px", border:"1.5px solid #FECACA", borderRadius:"8px", fontSize:"13px", outline:"none", background:"white" }}
                            />
                            <button
                              onClick={handleReject}
                              disabled={rejecting}
                              style={{ padding:"9px 18px", background: rejecting ? "#9CA3AF" : "#DC2626", color:"white", border:"none", borderRadius:"8px", fontSize:"13px", fontWeight:"700", cursor: rejecting ? "not-allowed":"pointer", whiteSpace:"nowrap" }}
                            >
                              {rejecting ? "Rejecting..." : "Confirm Reject"}
                            </button>
                            <button
                              onClick={() => { setShowRejectInput(false); setRejectReason(""); }}
                              style={{ padding:"9px 14px", background:"white", color:"#6B7280", border:"1px solid #E5E7EB", borderRadius:"8px", fontSize:"13px", fontWeight:"600", cursor:"pointer" }}
                            >
                              Cancel
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* ── Standard actions ── */}
                  <div style={{ display: "flex", gap: "12px", width: "100%" }}>
                    {isEditing ? (
                      <>
                        <button
                          onClick={() => {
                            setIsEditing(false);
                            setEditData(null);
                          }}
                          style={{
                            flex: 1,
                            padding: "14px",
                            background: "white",
                            color: "#374151",
                            border: "2px solid #E5E7EB",
                            borderRadius: "12px",
                            fontSize: "15px",
                            fontWeight: "600",
                            cursor: "pointer",
                          }}
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleUpdate}
                          disabled={saving}
                          style={{
                            flex: 1, padding: "14px",
                            background: saving ? "#6EE7B7" : "#10B981",
                            color: "white", border: "none", borderRadius: "12px",
                            fontSize: "15px", fontWeight: "600",
                            cursor: saving ? "not-allowed" : "pointer",
                            display: "flex", alignItems: "center",
                            justifyContent: "center", gap: "8px",
                          }}
                        >
                          {saving
                            ? <><span style={{ width:"16px", height:"16px",
                                border:"3px solid rgba(255,255,255,0.4)",
                                borderTop:"3px solid white", borderRadius:"50%",
                                animation:"spin 0.8s linear infinite",
                                display:"inline-block" }} /> Saving...</>
                            : "Save Changes"
                          }
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => setDetailsOpen(false)}
                          style={{
                            flex: 1,
                            padding: "14px",
                            background: "white",
                            color: "#374151",
                            border: "2px solid #E5E7EB",
                            borderRadius: "12px",
                            fontSize: "15px",
                            fontWeight: "600",
                            cursor: "pointer",
                          }}
                        >
                          Close
                        </button>
                        <button
                          onClick={handleDownloadReport}
                          disabled={downloadLoading}
                          style={{
                            flex: 1, padding: "14px",
                            background: downloadLoading ? "#9CA3AF"
                              : selectedJob.job_type === "maintenance"
                                ? "linear-gradient(135deg, #C026D3 0%, #86198F 100%)"
                                : "linear-gradient(135deg, #667EEA 0%, #764BA2 100%)",
                            color: "white", border: "none", borderRadius: "12px",
                            fontSize: "15px", fontWeight: "600",
                            cursor: downloadLoading ? "not-allowed" : "pointer",
                            display: "flex", alignItems: "center",
                            justifyContent: "center", gap: "8px",
                          }}
                        >
                          {downloadLoading
                            ? <><span style={{ width:"16px", height:"16px",
                                border:"3px solid rgba(255,255,255,0.4)",
                                borderTop:"3px solid white", borderRadius:"50%",
                                animation:"spin 0.8s linear infinite",
                                display:"inline-block" }} /> Generating...</>
                            : <><DownloadIcon style={{ fontSize:"20px" }} /> Download Report</>
                          }
                        </button>
                      </>
                    )}
                  </div>
                </Modal.Footer>
              </>
            )
          )}
        </div>
      </Modal>
    </div>
  );
};

export default JobHistory;